import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setTabExpandedSections, setTabRefAgentExpanded } from '../../../store/genbiSlice';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import { StatusBadge, StatusBadgeSm } from '../../utils/StatusBadge';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import DocumentSelector from './DocumentSelector';
import DocumentPreview from '../../utils/DocumentPreview';
import GenbiResponseComponent from './GenbiResponseComponent';
import AgentTranslationModal from './AgentTranslationModal';
import { flushSync, createPortal } from 'react-dom';
import ReportModalForm from './ReportModalForm';
import { ColorPickerButton } from './ReportFormattingModal';
import ValueMessageSelector from './ValueMessageSelector';
import ToggleMenu from './ToggleMenu';
import ConfirmationModal from '../../utils/ConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import VersionSelector from './VersionSelector';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import AgentConfigure from './AgentConfigure';
import AgentVersionComparePanel from './AgentVersionComparePanel';
import FilterPickerModal from './FilterPickerModal';
import SyncDocsModal from './SyncDocsModal';
import TableStructureSelector from './TableStructureSelector';

// ── Prompt Version Selector — mirrors VersionSelector.jsx structure exactly ──
const PromptVersionSelector = ({ promptVersions, selectedPromptVersion, onShow, onRestore, isLocked }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [promptVersions]);

  if (!promptVersions || promptVersions.length === 0) return null;

  const latestVersion = promptVersions[0];
  const activeVersion = selectedPromptVersion || latestVersion.version;

  return (
    <div ref={containerRef} className="d-inline-block position-relative ms-2">
      <button
        type="button"
        className="ev-message-card1 ev-radius-large d-flex align-items-center gap-0 prompt-version-trigger ms-1 mb-1 avatar-tooltip"
        onClick={() => !isLocked && setIsOpen(!isOpen)}
        data-tooltip={isLocked ? 'Agent is locked' : undefined}
      >
        <span className="ev-fs-10 ev-reg-p-blue">Prompt Version&nbsp;</span>
        <span className="ev-fs-10 ev-reg-p-blue">{activeVersion}</span>
        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'} ev-fs-9 ev-reg-p-blue ms-1`}></i>
      </button>

      {isOpen && (
        <div className="ev-card-4 position-absolute top-100 mt-2 p-2 z-1000" style={{ minWidth: '320px' }}>
          <div className="scrollable-element vh-20 p-1">
            {promptVersions.map(pv => {
              const versionName = `Prompt Version ${pv.version}`;
              const isLatest = pv.version === latestVersion.version;
              return (
                <div key={pv.version} className="ev-card-7 d-flex justify-content-between align-items-center mb-2 p-2 ev-radius-xsmall">
                  <div>
                    <span className="ev-small-p-dark me-1">{versionName}</span>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                      disabled={isLatest || isLocked}
                      onClick={() => {
                        onRestore(pv.version, versionName);
                        setIsOpen(false);
                      }}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg ms-1"
                      disabled={isLocked}
                      onClick={() => {
                        onShow(pv.version);
                        setIsOpen(false);
                      }}
                    >
                      Show
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const GenbiTemplateTabViewer = ({
  templateId,
  templateName,
  fabricId,
  templateDataCache,
  setTemplateDataCache,
  onError,
  onProcessingStart,
  onProcessingEnd,
  onRefreshTemplateList,
  vmConfig,
  onTemplateNameSaved,
  langId
}) => {
   const dispatch = useDispatch();
  const persistedExpandedSections = useSelector(state => state.genbi.tabExpandedSections[templateId]);
  const persistedRefAgentExpanded = useSelector(state => state.genbi.tabRefAgentExpanded[templateId]);
  const [formData, setFormData] = useState({
    template_name: '',
    fabric_id: null,
    fabric_name: '',
    sectionsDict: {},
    sectionOrder: [],
    vm_config: false,
    lang_id: 1
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!templateDataCache[templateId]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState(null);
  const [fabricTreeData, setFabricTreeData] = useState([]);
  const [tableStructures, setTableStructures] = useState([]);
  const [tableStructuresLoading, setTableStructuresLoading] = useState(true);
  const [documentSelectorOpen, setDocumentSelectorOpen] = useState(false);
  const [currentAgentIndex, setCurrentAgentIndex] = useState({ sectionKey: null, agentKey: null });
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const [showRunConfirmation, setShowRunConfirmation] = useState(false);
  const [runType, setRunType] = useState('');
  const [expandedSections, setExpandedSectionsLocal] = useState(persistedExpandedSections || {});

  // Ref agent selector expand state (persisted across modal open/close & tab switches)
  const [refAgentExpanded, setRefAgentExpandedLocal] = useState(persistedRefAgentExpanded || {});
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  // Wrappers: update local state + sync to Redux for persistence across tab switches
  const setExpandedSections = (updater) => {
    setExpandedSectionsLocal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      dispatch(setTabExpandedSections({ tabId: templateId, expandedSections: next }));
      return next;
    });
  };

  const setRefAgentExpanded = (updater) => {
    setRefAgentExpandedLocal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      dispatch(setTabRefAgentExpanded({ tabId: templateId, expandedSections: next }));
      return next;
    });
  };

  const [agentFeedback, setAgentFeedback] = useState({});
  const [agentGrounding, setAgentGrounding] = useState({});
  const [singleAgentExecution, setSingleAgentExecution] = useState({ agentId: null, feedback: null });
  const [agentQuickEdit, setAgentQuickEdit] = useState({}); // agentId -> bool (Quick Edit / non-CoT toggle)
  const templateStateCache = useRef({});
  // Selection → feedback attachment (per-agent, keyed by agent.id)
  const [agentSelectedText, setAgentSelectedText] = useState({});   // agentId -> string
  const [agentSelectedContext, setAgentSelectedContext] = useState({}); // agentId -> string (e.g. "table cell (column \"X\", row \"Y\")")
  const [agentPreviewOpen, setAgentPreviewOpen]   = useState({});   // agentId -> bool
  const [selectionFloatBtn, setSelectionFloatBtn] = useState(null); // { agentId, x, y, text } | null
  const responseWrapperRefs = useRef({});                            // agentId -> DOM node
  const [masterFeedback, setMasterFeedback] = useState('');

  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const scrollContainerRef = useRef(null);

  const savedExpandedStateRef = useRef({});

  const [vmSelectorOpen, setVmSelectorOpen] = useState(false);
  const [currentSectionForVM, setCurrentSectionForVM] = useState(null);
  const [valueMessages, setValueMessages] = useState([]);
  const [vmLoading, setVmLoading] = useState(false);
  const [vmSearch, setVmSearch] = useState("");
  const [vmSearchInput, setVmSearchInput] = useState("");
  const [vmPage, setVmPage] = useState(1);
  const [selectedVMIds, setSelectedVMIds] = useState([]);
  const [vmCategory, setVmCategory] = useState("");
  const [vmAddedBy, setVmAddedBy] = useState("");

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);

  // Comment processing states
  const [isProcessingComments, setIsProcessingComments] = useState(false);
  const [commentProcessingMessage, setCommentProcessingMessage] = useState('');
  const [agentsWithComments, setAgentsWithComments] = useState(new Set());
  const [commentFeedbackData, setCommentFeedbackData] = useState({});
  const [showCommentSuccessModal, setShowCommentSuccessModal] = useState(false);
  const [commentProcessingStats, setCommentProcessingStats] = useState(null);
  const [commentFeedbackList, setCommentFeedbackList] = useState([]);
  const [commentPage, setCommentPage] = useState(1);
  const [showVerifyCommentsConfirmation, setShowVerifyCommentsConfirmation] = useState(false);
  const [verifyCommentsMode, setVerifyCommentsMode] = useState('all');
  const [verifyCommentsCount, setVerifyCommentsCount] = useState(0);
  const fileInputRef = useRef(null);
  const [commentNavExpanded, setCommentNavExpanded] = useState({});
  const [originalCommentsModalOpen, setOriginalCommentsModalOpen] = useState(false);
  const [selectedAgentForComments, setSelectedAgentForComments] = useState(null);
  const [showDiscardCommentModal, setShowDiscardCommentModal] = useState(false);
  const [discardCommentInfo, setDiscardCommentInfo] = useState(null);
  const [showDiscardAllCommentsModal, setShowDiscardAllCommentsModal] = useState(false);

  const [restoreVersionInfo, setRestoreVersionInfo] = useState({ isOpen: false, agentId: null, version: null, versionName: '', docsChanged: false, restoreDocs: false, versionDocIds: [], currentDocIds: [], docNameMap: {} });

  const [selectedVersions, setSelectedVersions] = useState({});
  const [openVersionDropdown, setOpenVersionDropdown] = useState(null);

  const [comparePanelState, setComparePanelState] = useState(null);

  const [restorePromptVersionInfo, setRestorePromptVersionInfo] = useState({ isOpen: false, agentId: null, version: null, versionName: '' });
  const [selectedPromptVersions, setSelectedPromptVersions] = useState({});
  const [openPromptVersionDropdown, setOpenPromptVersionDropdown] = useState(null);
  const [promptVersionPreviewAgents, setPromptVersionPreviewAgents] = useState(new Set());
  const [showPromptVersionWarning, setShowPromptVersionWarning] = useState(false);
  const [pendingPromptVersionShow, setPendingPromptVersionShow] = useState(null);

  const [isGenReportModalVisible, setIsGenReportModalVisible] = useState(false)
  const [activePromptTab, setActivePromptTab] = useState({});
  const [openParagraphDropdown, setOpenParagraphDropdown] = useState({});
  const dropdownRef = useRef(null);
  const [openAgentSettingsDropdown, setOpenAgentSettingsDropdown] = useState(null);
  const settingsDropdownRef = useRef(null);

  // Special Section Config dropdown
  const [openSpecialSectionConfig, setOpenSpecialSectionConfig] = useState(null);
  const specialSectionConfigRef = useRef(null);

  // Section Type dropdown (Autogen / List of Figures / List of Tables / Abbreviations)
  const [openSectionTypeDropdown, setOpenSectionTypeDropdown] = useState(null);
  const sectionTypeDropdownRef = useRef(null);
  const [templateData, setTemplateData] = useState({
    agent_count: 0,
    completed_agents: 0,
    running_agents: 0,
    failed_agents: 0,
    pending_agents: 0,
    status: '',
    context_mapping_status: ''
  });

  const [configureModalOpen, setConfigureModalOpen] = useState(false);
  const [currentAgentConfig, setCurrentAgentConfig] = useState({
    sectionKey: null,
    agentKey: null,
  });

  // Inline translate dropdown state: { sectionKey, agentKey } or null
  const [translateDropdown, setTranslateDropdown] = useState(null);
  const [allLanguages, setAllLanguages] = useState([]);

  // FILTER & SELECTION ---
  const [agentFilter, setAgentFilter] = useState(['ALL']);
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());

  const [selectDropdownOpen, setSelectDropdownOpen] = useState(false);

  // Sync Docs Modal
  const [syncDocsModalOpen, setSyncDocsModalOpen] = useState(false);
  const [syncDocsChanges, setSyncDocsChanges] = useState([]);
  const [syncDocsLoading, setSyncDocsLoading] = useState(false);

  // Filter & Pick Modal — opens when user clicks the Filter Agents button
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);

  const selectDropdownRef = useRef(null);

  const prevTemplateIdRef = useRef(templateId);
  // Save/restore per-template state when switching between templates
  useEffect(() => {
    const prevId = prevTemplateIdRef.current;
    if (prevId !== templateId) {
      templateStateCache.current[prevId] = {
        agentFeedback, agentsWithComments, agentQuickEdit,
      };
      const cached = templateStateCache.current[templateId];
      setSelectedAgentIds(new Set());
      setAgentFeedback(cached?.agentFeedback || {});
      setAgentsWithComments(cached?.agentsWithComments || new Set());
      setAgentQuickEdit(cached?.agentQuickEdit || {});
      prevTemplateIdRef.current = templateId;
    }
  }, [templateId]);

  // Click-outside + scroll handler for select dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectDropdownRef.current && !selectDropdownRef.current.contains(e.target)) {
        setSelectDropdownOpen(false);
      }
    };
    const handleScroll = (e) => {
      if (selectDropdownRef.current && selectDropdownRef.current.contains(e.target)) return;
      setSelectDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const isAgentVisible = (agent) => {
    if (!agent) return false;
    const status = agent.status || 'PENDING';
    if (agentFilter.includes('ALL')) return true;
    if (agentFilter.includes('PENDING') && (status === 'PENDING' || status === 'NOT_BEGAN' || status === '')) return true;
    return agentFilter.includes(status);
  };


  const isSectionVisibleUnderFilter = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;
    if (agentFilter.includes('ALL')) return true;

    // Check direct agents in this section
    const hasVisibleAgent = section.agentOrder.some(agentKey => {
      return isAgentVisible(section.agentsDict[agentKey]);
    });
    if (hasVisibleAgent) return true;

    // Check child sections
    const childSections = formData.sectionOrder.filter(key => {
      const child = formData.sectionsDict[key];
      return child && child.parent_section_id === sectionKey;
    });
    
    return childSections.some(childKey => isSectionVisibleUnderFilter(childKey));
  };

  const getVisibleAgentIdsInSection = (sectionKey) => {
    let ids = [];
    const section = formData.sectionsDict[sectionKey];
    if (!section) return ids;

    section.agentOrder.forEach(agentKey => {
      const agent = section.agentsDict[agentKey];
      // only include agents that have an ID (saved to DB) and are visible
      if (agent && agent.id && isAgentVisible(agent)) {
        ids.push(agent.id);
      }
    });

    const childSections = formData.sectionOrder.filter(key => formData.sectionsDict[key]?.parent_section_id === sectionKey);
    childSections.forEach(childKey => {
      ids = [...ids, ...getVisibleAgentIdsInSection(childKey)];
    });
    return ids;
  };

  const getAllAgentIdsInTemplate = () => {
    const ids = [];
    formData.sectionOrder.forEach(sectionKey => {
      const section = formData.sectionsDict[sectionKey];
      if (!section) return;
      section.agentOrder.forEach(agentKey => {
        const agent = section.agentsDict[agentKey];
        if (agent && agent.id) ids.push(agent.id);
      });
    });
    return ids;
  };

  const handleSectionCheckbox = (sectionKey, checked) => {
    const visibleIds = getVisibleAgentIdsInSection(sectionKey);
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      visibleIds.forEach(id => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const toggleAgentSelection = (agentId) => {
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  const selectAllVisible = () => {
    const next = new Set(selectedAgentIds);
    formData.sectionOrder.forEach(sectionKey => {
      // Start from root sections to avoid duplicating logic
      if (!formData.sectionsDict[sectionKey]?.parent_section_id) {
         const ids = getVisibleAgentIdsInSection(sectionKey);
         ids.forEach(id => next.add(id));
      }
    });
    setSelectedAgentIds(next);
  };

  const deselectAllVisible = () => {
    const next = new Set(selectedAgentIds);
    formData.sectionOrder.forEach(sectionKey => {
      if (!formData.sectionsDict[sectionKey]?.parent_section_id) {
         const ids = getVisibleAgentIdsInSection(sectionKey);
         ids.forEach(id => next.delete(id));
      }
    });
    setSelectedAgentIds(next);
  };

  const toggleFilter = (value) => {
    setAgentFilter(prev => {
      if (value === 'ALL') return ['ALL'];
      let updated = prev.filter(v => v !== 'ALL');
      if (updated.includes(value)) updated = updated.filter(v => v !== value);
      else updated.push(value);
      return updated.length ? updated : ['ALL'];
    });
  };

  const handleSelectAll = useCallback(() => {
    selectAllVisible();
    setSelectDropdownOpen(false);
  }, [selectedAgentIds, formData.sectionOrder, formData.sectionsDict, agentFilter]);

  const handleDeselectAll = useCallback(() => {
    deselectAllVisible();
    setSelectDropdownOpen(false);
  }, [selectedAgentIds, formData.sectionOrder, formData.sectionsDict, agentFilter]);

  const handleStatusSelection = useCallback((status, add) => {
    if (status === 'ALL') {
      add ? selectAllVisible() : deselectAllVisible();
      return;
    }
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      Object.values(formData.sectionsDict).forEach(sec => {
        sec?.agentOrder?.forEach(aKey => {
          const ag = sec.agentsDict[aKey];
          if (ag?.id) {
            const st = ag.status || 'PENDING';
            const match = status === 'PENDING'
              ? (st === 'PENDING' || st === 'NOT_BEGAN' || st === '')
              : st === status;
            if (match) add ? next.add(ag.id) : next.delete(ag.id);
          }
        });
      });
      return next;
    });
  }, [formData.sectionsDict, selectAllVisible, deselectAllVisible]);

  const COMMENTS_PER_PAGE = 5;

  // Expand/Collapse All
  const getExpandableSectionKeys = () => {
    return (formData.sectionOrder || []).filter((k) => hasAnyContent(k));
  };

  const areAllExpandableExpanded = () => {
    const keys = getExpandableSectionKeys();
    if (keys.length === 0) return false;
    return keys.every((k) => expandedSections[k]);
  };

  const getParagraphStyleOptions = () => {
    const paragraphConfigs = Array.isArray(formData.format_json?.paragraph)
      ? formData.format_json.paragraph
      : [];

    const customParagraphs = paragraphConfigs.filter(
      (p) => (p?.id || "default") !== "default"
    );

    return customParagraphs.map((p, index) => ({
      label: p.name || `Paragraph (Style ${index + 1})`,
      value: p.level ?? (index + 1),
    }));
  };

  const toggleParagraphDropdown = (agentKey) => {
    setOpenParagraphDropdown((prev) => (prev === agentKey ? null : agentKey));
  };

  const closeParagraphDropdown = () => {
    setOpenParagraphDropdown(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeParagraphDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleSettingsClickOutside = (event) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setOpenAgentSettingsDropdown(null);
      }
    };
    const handleSettingsScroll = (e) => {
      if (settingsDropdownRef.current && settingsDropdownRef.current.contains(e.target)) return;
    };
    document.addEventListener("mousedown", handleSettingsClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleSettingsClickOutside);
    };
  }, []);

  // Special Section Config click-outside
  useEffect(() => {
    const handleSpecialConfigClickOutside = (event) => {
      if (specialSectionConfigRef.current && !specialSectionConfigRef.current.contains(event.target)) {
        setOpenSpecialSectionConfig(null);
      }
    };
    const handleSpecialConfigScroll = () => setOpenSpecialSectionConfig(null);
    document.addEventListener("mousedown", handleSpecialConfigClickOutside);
    document.addEventListener("scroll", handleSpecialConfigScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleSpecialConfigClickOutside);
      document.removeEventListener("scroll", handleSpecialConfigScroll, true);
    };
  }, []);

  const toggleSectionTypeDropdown = (sectionKey) => {
    setOpenSectionTypeDropdown((prev) => (prev === sectionKey ? null : sectionKey));
  };

  const closeSectionTypeDropdown = () => {
    setOpenSectionTypeDropdown(null);
  };

  useEffect(() => {
    const handleClickOutsideSectionType = (event) => {
      if (sectionTypeDropdownRef.current && !sectionTypeDropdownRef.current.contains(event.target)) {
        closeSectionTypeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutsideSectionType);
    return () => document.removeEventListener("mousedown", handleClickOutsideSectionType);
  }, []);

  const handleToggleExpandCollapseAll = () => {
    const keys = getExpandableSectionKeys();
    if (keys.length === 0) return;

    const shouldExpand = !keys.every((k) => expandedSections[k]);
    setExpandedSections((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = shouldExpand;
      });
      return next;
    });
  };

  // Distinguish user edit from api edits
  const isUserEditRef = useRef(false);
  const savedTemplateFingerprintRef = useRef(null);
  const navigate = useNavigate();
  
  const API_BASE_URL = '/pceapi/genbi';
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Fetch Available Languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/langs`, { headers });
        if (response.data.status === 'success' && response.data.langs) {
          const langsArray = Object.entries(response.data.langs).map(([id, name]) => ({
            id: parseInt(id),
            name
          }));
          setAllLanguages(langsArray);
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    };
    fetchLanguages();
  }, []);

  // Fetch Table Structures on mount + on refresh — shared across all agents to avoid N calls
  useEffect(() => {
    const fetchTableStructures = async () => {
      setTableStructuresLoading(true);
      try {
        const resp = await axios.post(`${API_BASE_URL}/list-table-structures`, {}, { headers });
        if (resp.data.status === 'success' && resp.data.table_structures) {
          setTableStructures(resp.data.table_structures);
        }
      } catch (err) {
        console.error('Failed to fetch table structures:', err);
      } finally {
        setTableStructuresLoading(false);
      }
    };
    fetchTableStructures();
  }, [refreshKey]);

  const openVMSelector = (sectionKey) => {
    setCurrentSectionForVM(sectionKey);
    setVmSelectorOpen(true);
  };

  const handleVmSearchInputChange = (e) => {
    setVmSearchInput(e.target.value);
  };

  const handleVmSearch = () => {
    setVmSearch(vmSearchInput);
    setVmPage(1);
  };

  const handleVmKeyPress = (e) => {
    if (e.key === "Enter") {
      handleVmSearch();
    }
  };

  const buildCommentNavigationStructure = () => {
  const structure = {};
  
  // Iterate through sections in order
  formData.sectionOrder.forEach(sectionKey => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return;
    
    // Check if section or any of its agents have comments
    const sectionHasDirectComments = sectionHasFeedback(sectionKey);
    const agentsWithCommentsInSection = [];
    
    if (section.agentOrder) {
      section.agentOrder.forEach(agentKey => {
        const agent = section.agentsDict[agentKey];
        if (agent && agentsWithComments.has(agent.id)) {
          agentsWithCommentsInSection.push({
            agentKey,
            agentId: agent.id,
            agentName: agent.agent_name || `Agent ${agent.agent_ordering_id}`,
            sectionOrdering: section.section_ordering_id,
            agentOrdering: agent.agent_ordering_id,
            commentData: commentFeedbackData[agent.id]
          });
        }
      });
    }
    
    if (sectionHasDirectComments || agentsWithCommentsInSection.length > 0) {
      // Determine if this is a parent or child section
      const isParentSection = !section.parent_section_id;
      const parentSectionKey = section.parent_section_id;
      
      if (isParentSection) {
        // This is a top-level section
        if (!structure[sectionKey]) {
          structure[sectionKey] = {
            sectionKey,
            sectionName: section.section_name,
            sectionOrdering: section.section_ordering_id,
            subsections: {},
            agents: []
          };
        }
        structure[sectionKey].agents.push(...agentsWithCommentsInSection);
      } else {
        // This is a subsection
        if (!structure[parentSectionKey]) {
          const parentSection = formData.sectionsDict[parentSectionKey];
          structure[parentSectionKey] = {
            sectionKey: parentSectionKey,
            sectionName: parentSection?.section_name || 'Section',
            sectionOrdering: parentSection?.section_ordering_id || '',
            subsections: {},
            agents: []
          };
        }
        
        structure[parentSectionKey].subsections[sectionKey] = {
          sectionKey,
          sectionName: section.section_name,
          sectionOrdering: section.section_ordering_id,
          agents: agentsWithCommentsInSection
        };
      }
    }
  });
  
  return structure;
};

// Add function to count total comments in a section/subsection
const countCommentsInSection = (sectionData) => {
  let count = sectionData.agents.length;
  if (sectionData.subsections) {
    Object.values(sectionData.subsections).forEach(subsection => {
      count += subsection.agents.length;
    });
  }
  return count;
};

// Add function to handle opening original comments modal
const openOriginalCommentsModal = (sectionKey, agentKey) => {
  setSelectedAgentForComments({ sectionKey, agentKey });
  setOriginalCommentsModalOpen(true);
};

// Add function to toggle section expansion in navigation
const toggleCommentNavSection = (sectionKey) => {
  setCommentNavExpanded(prev => ({
    ...prev,
    [sectionKey]: !prev[sectionKey]
  }));
};
  const fetchValueMessages = async () => {
    setVmLoading(true);
    try {
      const requestBody = {
        template_id: templateId,
        fabric_id: fabricId,
        page: 1,
        size: 1000
      };

      if (vmCategory) {
        requestBody.category = vmCategory;
      }
      if (vmAddedBy) {
        requestBody.added_by = String(vmAddedBy).toUpperCase();
      }

      const response = await axios.post(
        `${API_BASE_URL}/value-message/list-value-messages`,
        requestBody,
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to load value messages');
        return;
      }

      setValueMessages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching value messages:', error);
      onError(error.message || 'Failed to load value messages');
    } finally {
      setVmLoading(false);
    }
  };

  const handlePreviewDocument = (docId, documentName) => {
    setPreviewDocId(docId);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };

  const handleVMSelection = (sectionKey, selectedVMIds) => {
    isUserEditRef.current = true;
    setFormData(prev => ({
      ...prev,
      sectionsDict: {
        ...prev.sectionsDict,
        [sectionKey]: {
          ...prev.sectionsDict[sectionKey],
          vm_mappings: selectedVMIds
        }
      }
    }));
    setVmSelectorOpen(false);
    setCurrentSectionForVM(null);
    setHasPendingChanges(true);
  };

  useEffect(() => {
    if (vmSelectorOpen && currentSectionForVM) {
      const section = formData.sectionsDict[currentSectionForVM];
      setSelectedVMIds(section?.vm_mappings || []);
    }
  }, [vmSelectorOpen, currentSectionForVM]);

  const getSelectedVMCount = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    return section?.vm_mappings?.length || 0;
  };

  const saveCurrentState = () => {
    if (scrollContainerRef.current) {
      setSavedScrollPosition(scrollContainerRef.current.scrollTop);
    }

    savedExpandedStateRef.current = { ...expandedSections };
  };
    /**
   * Open discard comment modal
   */
  const openDiscardCommentModal = (sectionKey, agentKey) => {
    const section = formData.sectionsDict[sectionKey];
    const agent = section?.agentsDict[agentKey];
    
    if (!agent) return;
    
    const commentData = getCommentData(sectionKey, agentKey);
    const feedback = getAgentFeedback(sectionKey, agentKey);
    
    setDiscardCommentInfo({
      sectionKey,
      agentKey,
      agentId: agent.id,
      agentName: agent.agent_name || `Agent ${agent.agent_ordering_id}`,
      sectionName: section.section_name,
      sectionOrdering: section.section_ordering_id,
      agentOrdering: agent.agent_ordering_id,
      consolidatedFeedback: feedback,
      originalComments: commentData?.original_comments || [],
      commentCount: commentData?.comment_count || 0
    });
    
    setShowDiscardCommentModal(true);
  };

  /**
   * Execute discard comment
   */
  const executeDiscardComment = () => {
    if (!discardCommentInfo) return;
    
    const { sectionKey, agentKey, agentId } = discardCommentInfo;
    
    // Clear feedback for this agent
    setAgentFeedback(prev => {
      const newFeedback = { ...prev };
      delete newFeedback[agentId];
      return newFeedback;
    });
    
    // Remove from agents with comments
    setAgentsWithComments(prev => {
      const newSet = new Set(prev);
      newSet.delete(agentId);
      return newSet;
    });
    
    // Remove from comment feedback data
    setCommentFeedbackData(prev => {
      const newData = { ...prev };
      delete newData[agentId];
      return newData;
    });
    
    // Remove from comment feedback list
    setCommentFeedbackList(prev => 
      prev.filter(item => item.agentId !== agentId)
    );
    
    // Close modal and reset
    setShowDiscardCommentModal(false);
    setDiscardCommentInfo(null);
  };
  // Add this useEffect after comment processing completes
  useEffect(() => {
    if (showCommentSuccessModal && commentFeedbackList.length > 0) {
      // Auto-expand all parent sections on modal open
      const structure = buildCommentNavigationStructure();
      const autoExpandState = {};
      
      Object.keys(structure).forEach(sectionKey => {
        autoExpandState[sectionKey] = true; // Expand parent sections by default
      });
      
      setCommentNavExpanded(autoExpandState);
    }
  }, [showCommentSuccessModal]);

  const restoreCurrentState = () => {
    if (Object.keys(savedExpandedStateRef.current).length > 0) {
      setExpandedSections(savedExpandedStateRef.current);
    }
    setTimeout(() => {
      if (scrollContainerRef.current && savedScrollPosition > 0) {
        scrollContainerRef.current.scrollTop = savedScrollPosition;
      }
    }, 100);
  };

  const updateAgentFeedback = (sectionKey, agentKey, feedback) => {
    const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
    if (agentId) {
      setAgentFeedback(prev => ({
        ...prev,
        [agentId]: feedback
      }));
    }
  };

  const getAgentFeedback = (sectionKey, agentKey) => {
    const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
    return agentId ? agentFeedback[agentId] || '' : '';
  };

  const getAgentById = (agentId) => {
    for (const sKey of (formData.sectionOrder || [])) {
      const section = formData.sectionsDict?.[sKey];
      for (const aKey of (section?.agentOrder || [])) {
        const ag = section.agentsDict?.[aKey];
        if (ag?.id === agentId) return ag;
      }
    }
    return null;
  };

  const getAgentPersistRunOptions = (agent) => {
    const preserve = !!agent?.preserve_response;
    const amend = !!agent?.amend_response;
    return {
      preserve_response: preserve,
      amend_response: amend,
      response_colour: (preserve || amend) ? (agent?.response_colour || null) : null,
    };
  };

  const getSelectionContext = (sel, wrapper) => {
    if (!sel || sel.rangeCount === 0) return null;
    const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s || '');

    const range = sel.getRangeAt(0);
    let startNode = range.startContainer;
    if (startNode.nodeType === Node.TEXT_NODE) startNode = startNode.parentNode;
    if (!startNode || !wrapper.contains(startNode)) return null;

    // Walk up
    let cursor = startNode;
    while (cursor && cursor !== wrapper && cursor.nodeType === Node.ELEMENT_NODE) {
      const tag = cursor.tagName;

      if (tag === 'TABLE') {
        // Find the cell we're in
        const cell = (startNode.closest && startNode.closest('td, th'));
        if (cell && cursor.contains(cell)) {
          const row = cell.parentElement;
          const cellIdx = Array.from(row.children).indexOf(cell);
          // Column header: first row's cell at same index (works for <thead> or <tbody> tables)
          const headerRow = cursor.querySelector('thead tr') || cursor.querySelector('tr');
          const headerCell = headerRow && headerRow.children[cellIdx];
          const columnLabel = headerCell && headerCell !== cell
            ? headerCell.innerText.trim()
            : null;
          // Row label: first cell of the row (if it's not the selected cell itself)
          const firstCell = row.children[0];
          const rowLabel = firstCell && firstCell !== cell
            ? firstCell.innerText.trim()
            : null;
          const parts = [];
          if (columnLabel) parts.push(`column "${truncate(columnLabel, 60)}"`);
          if (rowLabel)    parts.push(`row "${truncate(rowLabel, 60)}"`);
          return `table cell${parts.length ? ' (' + parts.join(', ') + ')' : ''}`;
        }
        return 'table';
      }

      if (/^H[1-6]$/.test(tag)) return `heading (${tag.toLowerCase()})`;
      if (tag === 'LI')         return 'list item';
      if (tag === 'PRE' || tag === 'CODE') return 'code block';
      if (tag === 'BLOCKQUOTE') return 'quote';

      cursor = cursor.parentNode;
    }
    return null; // plain paragraph text — no special context
  };

  const handleResponseMouseUp = (agentId) => () => {
    // Defer so getSelection() reflects the just-completed selection
    setTimeout(() => {
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.isCollapsed) { setSelectionFloatBtn(null); return; }

      const text = sel.toString().trim();
      if (!text) { setSelectionFloatBtn(null); return; }

      const wrapper = responseWrapperRefs.current[agentId];
      if (!wrapper) return;

      // Only accept selections that started inside this wrapper
      const anchor = sel.anchorNode;
      if (!anchor || !wrapper.contains(anchor)) return;
      
      // Bail out if the selection is inside a rich-format editable area
      // (table cells, footnote input, table title) — those have their own toolbar
      const anchorEl = anchor.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor;
      if (anchorEl && anchorEl.closest('[contenteditable="true"]')) {
        setSelectionFloatBtn(null);
        return;
      }

      if (anchorEl && anchorEl.closest('[data-genbi-no-feedback-select]')) {
        setSelectionFloatBtn(null);
        return;
      }
      try {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const wrapRect = wrapper.getBoundingClientRect();
        const context = getSelectionContext(sel, wrapper);
        setSelectionFloatBtn({
          agentId,
          x: rect.left - wrapRect.left + rect.width / 2,
          y: rect.top - wrapRect.top,   // button sits above via CSS translate
          text,
          context,
        });
      } catch (_) {
        setSelectionFloatBtn(null);
      }
    }, 0);
  };

  const attachSelectionToFeedback = () => {
    if (!selectionFloatBtn) return;
    const { agentId, text, context } = selectionFloatBtn;
    setAgentSelectedText(prev => ({ ...prev, [agentId]: text }));
    setAgentSelectedContext(prev => ({ ...prev, [agentId]: context || null }));
    setAgentPreviewOpen(prev => ({ ...prev, [agentId]: false })); // start closed
    setSelectionFloatBtn(null);
    try { window.getSelection().removeAllRanges(); } catch (_) {}
  };

  const togglePreviewOpen = (agentId) => {
    setAgentPreviewOpen(prev => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  const removeSelectionAttachment = (agentId) => {
    setAgentSelectedText(prev    => { const n = { ...prev }; delete n[agentId]; return n; });
    setAgentSelectedContext(prev => { const n = { ...prev }; delete n[agentId]; return n; });
    setAgentPreviewOpen(prev     => { const n = { ...prev }; delete n[agentId]; return n; });
  };

  const clearSelectionAttachments = (agentIds) => {
    const ids = Array.isArray(agentIds) ? agentIds : [agentIds];
    setAgentSelectedText(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
    setAgentSelectedContext(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
    setAgentPreviewOpen(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  };

  /** Merge attached selection with typed feedback for submission. */
  const buildFinalFeedback = (agentId, typed) => {
    const attached = agentSelectedText[agentId];
    const context = agentSelectedContext[agentId];
    if (attached && attached.trim()) {
      const prefix = context
        ? `Regarding this text from a ${context}: "${attached}"`
        : `Regarding this text: "${attached}"`;
      return `${prefix}\n\nChange requested: ${typed || ''}`;
    }
    return typed || '';
  };

  const isLatestManualResponse = (agent) => {
    const latestSource = agent?.versions?.[0]?.source || '';
    return agent?.status === 'COMPLETED' && latestSource === 'manual';
  };

  const handleRunSingleAgent = (sectionKey, agentKey) => {
    const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
    if (!agent || !agent.id) return;

    if (promptVersionPreviewAgents.has(agent.id)) {
      setPendingPromptVersionShow({ agentId: agent.id, sectionKey, agentKey });
      setShowPromptVersionWarning(true);
      return;
    }

    const feedback = getAgentFeedback(sectionKey, agentKey);
    const lastRunDocIds = Array.isArray(agent.last_run_doc_ids) ? agent.last_run_doc_ids : [];
    const currentDocIds = (agent.document_mappings || []).filter(d => d.selected).map(d => d.doc_id);
    const docsStale = agent.status === 'COMPLETED' && agent.last_run_doc_ids != null && (
      currentDocIds.length !== lastRunDocIds.length
      || currentDocIds.some(id => !new Set(lastRunDocIds).has(id))
      || lastRunDocIds.some(id => !new Set(currentDocIds).has(id))
    );
    const quickEdit = agent.status === 'COMPLETED' && !agent._promptModified && !docsStale && !!agentQuickEdit[agent.id];
    const allowFeedback = !isLatestManualResponse(agent) || quickEdit;
    const finalFeedback = allowFeedback ? buildFinalFeedback(agent.id, feedback) : '';
    if (quickEdit && (!feedback || !feedback.trim())) {
      onError('Enter an exact change in Feedback, or switch to Thinking.');
      return;
    }
    const runOptions = getAgentPersistRunOptions(agent);
    setSingleAgentExecution({ agentId: agent.id, feedback: finalFeedback, use_feedback_editor: quickEdit, ...runOptions });
    setRunType('single_agent');
    setShowRunConfirmation(true);
  };

  /**
   * Get section ordering ID for an agent
   */
  const getAgentSectionOrdering = (agentId) => {
    for (const sectionKey of formData.sectionOrder) {
      const section = formData.sectionsDict[sectionKey];
      if (!section) continue;

      for (const agentKey of section.agentOrder || []) {
        const agent = section.agentsDict[agentKey];
        if (agent && agent.id === agentId) {
          return section.section_ordering_id;
        }
      }
    }
    return null;
  };

  /**
   * Get agents that reference (depend on) the given agent ID (reverse lookup)
   * Returns array of strings like ["S.3. Agent 1", "S.4. Agent 2"]
   */
  const getReferencedByAgentLabels = (agentId) => {
    const labels = [];
    for (const sectionKey of formData.sectionOrder) {
      const section = formData.sectionsDict[sectionKey];
      if (!section) continue;
      for (const agentKey of section.agentOrder || []) {
        const agent = section.agentsDict[agentKey];
        if (!agent) continue;
        // Check if this agent's reference_agent_ids contains our agentId
        if (agent.reference_agent_ids && agent.reference_agent_ids.includes(agentId)) {
          const sectionOrdering = section.section_ordering_id || '';
          const agentOrdering = agent.agent_ordering_id || 1;
          labels.push(`S.${sectionOrdering}. Agent ${agentOrdering}`);
        }
      }
    }
    return labels;
  };

  /**
   * Handle file upload for commented document
   */
  const handleCommentedDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'docx') {
      onError('Only DOCX files are supported for comment addressing');
      event.target.value = '';
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      onError('File size exceeds 50MB limit. Please upload a smaller document.');
      event.target.value = '';
      return;
    }

    // Process the file
    await processCommentedDocument(file);
    
    // Clear file input for re-upload
    event.target.value = '';
  };

  /**
   * Main function to process uploaded commented document
   */
  /**
 * Execute discard all comments
 */
const executeDiscardAllComments = () => {
  // Clear all feedback
  setAgentFeedback({});
  
  // Clear agents with comments
  setAgentsWithComments(new Set());
  
  // Clear comment data
  setCommentFeedbackData({});
  
  // Clear comment feedback list
  setCommentFeedbackList([]);
  
  // Close modal
  setShowDiscardAllCommentsModal(false);
};
  const processCommentedDocument = async (file) => {
    setIsProcessingComments(true);
    setCommentProcessingMessage('Uploading document...');
    onProcessingStart('Processing comments from document. This may take 2-3 minutes...');

    try {
      // Prepare form data
      const formDataUpload = new FormData();
      formDataUpload.append('template_id', templateId);
      formDataUpload.append('fabric_id', fabricId);
      formDataUpload.append('file', file);

      setCommentProcessingMessage('Extracting comments and mapping to agents...');

      // Upload and process
      const response = await axios.post(
        `${API_BASE_URL}/comments/upload-and-process`,
        formDataUpload,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Handle authentication
      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      // Handle failure
      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to process comments');
        return;
      }

      setCommentProcessingMessage('Consolidating feedback using AI...');

      // Extract data
      const { agent_feedbacks, total_agents, total_comments, processing_time } = response.data.data;

      // Handle no comments case
      if (!agent_feedbacks || Object.keys(agent_feedbacks).length === 0) {
        onError('No comments found in the document. Please add comments using Word and try again.');
        return;
      }

      // Update state with feedback
      const newAgentFeedback = {};
      const newCommentData = {};
      const agentsSet = new Set();
      const feedbackList = [];

      Object.entries(agent_feedbacks).forEach(([agentId, data]) => {
        const agentIdInt = parseInt(agentId);
        newAgentFeedback[agentIdInt] = data.consolidated_feedback;
        newCommentData[agentIdInt] = data;
        agentsSet.add(agentIdInt);

        // Build feedback list for modal
        feedbackList.push({
          agentId: agentIdInt,
          sectionOrdering: getAgentSectionOrdering(agentIdInt),
          consolidatedFeedback: data.consolidated_feedback,
          originalComments: data.original_comments || [],
          commentCount: data.comment_count || 0
        });
      });

      // Merge with existing feedback (don't overwrite manual feedback)
      setAgentFeedback(prevFeedback => ({
        ...prevFeedback,
        ...newAgentFeedback
      }));

      setCommentFeedbackData(newCommentData);
      setAgentsWithComments(agentsSet);
      setCommentFeedbackList(feedbackList);
      setCommentPage(1);
      
      // Store stats for success modal
      setCommentProcessingStats({
        total_agents,
        total_comments,
        processing_time
      });

      // Show success modal
      setShowCommentSuccessModal(true);

    } catch (error) {
      console.error('Error processing commented document:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to process commented document';
      onError(errorMessage);
    } finally {
      setIsProcessingComments(false);
      setCommentProcessingMessage('');
      onProcessingEnd();
    }
  };

  /**
   * Scroll to the first agent with comments
   */
  const scrollToFirstCommentedAgent = (agentsSet) => {
    if (!agentsSet || agentsSet.size === 0) return;

    // Find first section and agent with comments
    for (const sectionKey of formData.sectionOrder) {
      const section = formData.sectionsDict[sectionKey];
      if (!section || !section.agentOrder) continue;

      for (const agentKey of section.agentOrder) {
        const agent = section.agentsDict[agentKey];
        if (agent && agentsSet.has(agent.id)) {
          // Expand section if collapsed
          setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: true
          }));

          // Scroll to agent
          setTimeout(() => {
            const agentElement = document.querySelector(`[data-agent-id="${agent.id}"]`);
            if (agentElement) {
              agentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300);
          
          return;
        }
      }
    }
  };

  /**
   * Check if an agent has comments
   */
  const hasComments = (sectionKey, agentKey) => {
    const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
    return agent && agentsWithComments.has(agent.id);
  };

  /**
   * Get comment data for an agent
   */
  const getCommentData = (sectionKey, agentKey) => {
    const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
    return agent ? commentFeedbackData[agent.id] : null;
  };

  /**
   * Clear all comments
   */
  const clearAllComments = () => {
    if (agentsWithComments.size === 0) return;

    const confirmClear = window.confirm(
      `This will clear feedback for ${agentsWithComments.size} agent(s). Are you sure?`
    );

    if (!confirmClear) return;

    // Clear agents with comments
    setAgentsWithComments(new Set());
    setCommentFeedbackData({});
    
    // Clear feedback for commented agents
    setAgentFeedback(prev => {
      const newFeedback = { ...prev };
      agentsWithComments.forEach(agentId => {
        delete newFeedback[agentId];
      });
      return newFeedback;
    });
  };

  /**
   * Trigger file input click programmatically
   */
  const triggerCommentUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Helper to count selected agents that have feedback
   */
  const getSelectedAgentsWithFeedbackCount = () => {
    return Array.from(selectedAgentIds).filter(id => agentFeedback[id] && agentFeedback[id].trim()).length;
  };

  /**
   * Verify and address comments - handles both ALL and SELECTED
   */
  const handleVerifyAndAddressComments = async (selectedOnly = false) => {
    const agentsToRun = [];
    
    Object.entries(agentFeedback).forEach(([agentId, feedback]) => {
      const idInt = parseInt(agentId);
      if (feedback && feedback.trim()) {
        if (!selectedOnly || selectedAgentIds.has(idInt)) {
          agentsToRun.push({ agentId: idInt, feedback });
        }
      }
    });

    if (agentsToRun.length === 0) {
      onError(selectedOnly ? 'No selected agents have feedback to address' : 'No agents with feedback found to address');
      return;
    }

    setVerifyCommentsMode(selectedOnly ? 'selected' : 'all');
    setVerifyCommentsCount(agentsToRun.length);
    setShowVerifyCommentsConfirmation(true);
  };

  /**
   * Execute verify and address comments after confirmation
   */
  const executeVerifyAndAddressComments = async () => {
    setShowVerifyCommentsConfirmation(false);

    const agentsToRun = [];
    Object.entries(agentFeedback).forEach(([agentId, feedback]) => {
      const idInt = parseInt(agentId);
      if (feedback && feedback.trim()) {
        if (verifyCommentsMode !== 'selected' || selectedAgentIds.has(idInt)) {
          agentsToRun.push({ agentId: idInt, feedback });
        }
      }
    });

    if (agentsToRun.length === 0) return;

    if (onProcessingStart) {
      onProcessingStart(`Addressing feedback for ${agentsToRun.length} agent(s)...`);
    }

    try {
      if (hasPendingChanges) {
        await performSaveTemplate();
      }

      // --- API CALL LOGIC ---
      // Backend expects: agents: [{ agent_id, feedback }]
      const agentsPayload = agentsToRun.map(a => {
        const finalFeedback = buildFinalFeedback(a.agentId, a.feedback);
        return {
          agent_id: a.agentId,
          feedback: finalFeedback && finalFeedback.trim() ? finalFeedback.trim() : null,
          ...getAgentPersistRunOptions(getAgentById(a.agentId))
        };
      });

      // Single API Call instead of sequential loops
      const response = await axios.post(
        `${API_BASE_URL}/run-template`,
        {
          template_id: templateId,
          fabric_id: fabricId,
          master_feedback: null,
          agents: agentsPayload
        },
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        onError(response.data.message || `Failed to address comments`);
        return;
      }

      // Clear feedback ONLY for the agents that were successfully executed
      setAgentFeedback(prev => {
        const newFeedback = { ...prev };
        agentsToRun.forEach(a => delete newFeedback[a.agentId]);
        return newFeedback;
      });

      setAgentsWithComments(prev => {
        const newSet = new Set(prev);
        agentsToRun.forEach(a => newSet.delete(a.agentId));
        return newSet;
      });

      setCommentFeedbackData(prev => {
        const newData = { ...prev };
        agentsToRun.forEach(a => delete newData[a.agentId]);
        return newData;
      });

      clearSelectionAttachments(agentsToRun.map(a => a.agentId));

      // If we ran selected agents, deselect them after running
      if (verifyCommentsMode === 'selected') {
         setSelectedAgentIds(prev => {
           const newSet = new Set(prev);
           agentsToRun.forEach(a => newSet.delete(a.agentId));
           return newSet;
         });
      }

      setTimeout(() => {
        handleRefresh();
        if (onRefreshTemplateList) {
          onRefreshTemplateList();
        }
      }, 1000);

    } catch (error) {
      console.error('Error addressing comments:', error);
      onError(error.message || 'Failed to address comments');
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  /**
   * Check if section has feedback (directly or in children)
   */
  const sectionHasFeedback = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    // Check direct agents
    const hasDirectFeedback = (section.agentOrder || []).some(agentKey => {
      const agent = section.agentsDict[agentKey];
      return agent && agentsWithComments.has(agent.id);
    });

    if (hasDirectFeedback) return true;

    // Check child sections
    const childSections = formData.sectionOrder.filter(key => {
      const childSection = formData.sectionsDict[key];
      return childSection && childSection.parent_section_id === sectionKey;
    });

    return childSections.some(childKey => sectionHasFeedback(childKey));
  };

  /**
   * Check if section is a parent of a section with feedback
   */
  const isParentOfFeedbackSection = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    // Check all sections
    for (const key of formData.sectionOrder) {
      const childSection = formData.sectionsDict[key];
      if (!childSection || key === sectionKey) continue;

      // If this section has feedback and our section is its ancestor
      if (sectionHasFeedback(key) && isAncestor(sectionKey, key)) {
        return true;
      }
    }

    return false;
  };

  /**
   * Check if parentKey is an ancestor of childKey
   */
  const isAncestor = (parentKey, childKey) => {
    const childSection = formData.sectionsDict[childKey];
    if (!childSection) return false;

    if (childSection.parent_section_id === parentKey) return true;
    if (!childSection.parent_section_id) return false;

    return isAncestor(parentKey, childSection.parent_section_id);
  };

  const convertSectionsToDict = (sectionsArray) => {
    const sectionsDict = {};
    const sectionOrder = [];

    sectionsArray.forEach(section => {
      const key = section.id || section.temp_id || `temp_${Date.now()}_${Math.random()}`;

      const agentsDict = {};
      const agentOrder = [];

      if (section.agents) {
        section.agents.forEach(agent => {
          const agentKey = agent.id || agent.temp_id || `agent_temp_${Date.now()}_${Math.random()}`;
          agentsDict[agentKey] = { ...agent,
            reference_agent_ids: agent.reference_agent_ids || [],
            _savedAgentContext: agent.agent_context || '',
            _savedAdditionalContext: agent.additional_context || '',
            _promptModified: false,
            _modifiedOutsidePrompt: false
          };
          if (!agent.isDeleted) {
            agentOrder.push(agentKey);
          }
        });
      }

      const oid = section.section_ordering_id || '';
      const isSPrefix = oid.startsWith('S') || oid.startsWith('s');

      sectionsDict[key] = {
        ...section,
        is_summary_section: section.is_summary_section || isSPrefix,
        agentsDict,
        agentOrder,
        vm_mappings: section.vm_mappings || []
      };

      if (!section.isDeleted) {
        sectionOrder.push(key);
      }
    });

    return { sectionsDict, sectionOrder };
  };

  const convertToStructuredSections = (sectionsDict, sectionOrder) => {
    const structured = {
      new: [],
      modified: [],
      deleted: []
    };

    // Collect deleted sections ONCE (outside the loop to avoid N duplicates)
    Object.values(sectionsDict).forEach(section => {
      if (section.isDeleted && section.id) {
        structured.deleted.push({
          id: section.id,
        });
      }
    });

    sectionOrder.forEach(sectionKey => {
      const section = sectionsDict[sectionKey];

      const agents = section.agentOrder.map(agentKey => {
        const agent = section.agentsDict[agentKey];
        const isDirty = !agent.id || agent.isModified === true; // New agents are always dirty
        return {
          id: agent.id,
          temp_id: agent.temp_id,
          agent_name: agent.agent_name || '',
          agent_context: agent.agent_context || '',
          additional_context: agent.additional_context || null,
          is_locked: agent.is_locked || false,
          preserve_response: agent.preserve_response ?? false,
          amend_response: agent.amend_response ?? false,
          response_colour: agent.response_colour ?? null,
          rich_formatting: agent.rich_formatting ?? false,
          agent_ordering_id: agent.agent_ordering_id || 1,
          document_mappings: agent.document_mappings || [],
          reference_agent_ids: agent.reference_agent_ids || [],
          par_style: agent.par_style ?? null,
          table_structure_id: agent.table_structure_id ?? null,
          is_dirty: isDirty,
          explicit_delete: false,
          tag: agent.tag || null,
          _editedResponse: agent.editedResponse || null
        };
      });

      // Also include agents marked as isDeleted with explicit_delete flag
      Object.keys(section.agentsDict).forEach(agentKey => {
        const agent = section.agentsDict[agentKey];
        if (agent.isDeleted && agent.id) {
          agents.push({
            id: agent.id,
            agent_name: agent.agent_name || '',
            agent_context: agent.agent_context || '',
            additional_context: agent.additional_context || null,
            agent_ordering_id: agent.agent_ordering_id || 1,
            document_mappings: agent.document_mappings || [],
            reference_agent_ids: agent.reference_agent_ids || [],
            par_style: agent.par_style ?? null,
            is_dirty: false,
            explicit_delete: true,
            tag: agent.tag || null
          });
        }
      });

      const sectionData = {
        id: section.id,
        temp_id: section.temp_id,
        section_name: section.section_name || '',
        parent_section_id: section.parent_section_id,
        section_ordering_id: section.section_ordering_id || '1',
        is_summary_section: section.is_summary_section || false,
        autogen_after: section.autogen_after || null,
        special_section_config: section.is_summary_section ? {
          is_outside_border: section.special_section_config?.is_outside_border || false,
          heading_bg_color: section.special_section_config?.heading_bg_color || null,
        } : null,
        vm_mappings: section.vm_mappings || [],
        agents: agents
      };

      if (section.id) {
        structured.modified.push(sectionData);
      } else {
        structured.new.push(sectionData);
      }
    });

    return structured;
  };

  const buildTemplateFingerprint = (data) => {
    const structuredSections = convertToStructuredSections(data.sectionsDict, data.sectionOrder);
    const normalizeSections = (sections) => sections.map(section => ({
      ...section,
      agents: section.agents.map(({ is_dirty, ...agent }) => ({
        ...agent,
        document_mappings: (agent.document_mappings || [])
          .filter(mapping => mapping.selected !== false)
          .sort((a, b) => String(a.doc_id).localeCompare(String(b.doc_id)))
      }))
    }));

    return JSON.stringify({
      template_name: data.template_name || '',
      fabric_id: data.fabric_id ?? null,
      vm_config: !!data.vm_config,
      lang_id: data.lang_id || 1,
      global_prompt: data.global_prompt || '',
      format_json: data.format_json || null,
      sections: {
        new: normalizeSections(structuredSections.new),
        modified: normalizeSections(structuredSections.modified),
        deleted: structuredSections.deleted
      }
    });
  };

  const generateOrderingId = (parentKey, sectionsDict, sectionOrder) => {
    if (!parentKey) {
      // Exclude summary sections (S-prefix) from numeric ordering calculation
      const rootSections = sectionOrder.filter(key => {
        const section = sectionsDict[key];
        return section && !section.parent_section_id && !section.is_summary_section;
      });
      const maxNumber = rootSections.reduce((max, key) => {
        const section = sectionsDict[key];
        const orderingId = section?.section_ordering_id || '0';
        const num = parseInt(orderingId.split('.')[0]);
        return Math.max(max, isNaN(num) ? 0 : num);
      }, 0);
      const fmt = formData.format_json?.numberingFormat;
const startVal = (fmt === "none" || fmt === undefined) ? (formData.format_json?.numberingStartValue ?? 1) : 1;
return String(Math.max(maxNumber + 1, startVal));
    } else {
      const parentSection = sectionsDict[parentKey];
      if (!parentSection) return '1';

      const parentOrderingId = parentSection.section_ordering_id;
      const siblings = sectionOrder.filter(key => {
        const section = sectionsDict[key];
        return section && section.parent_section_id === parentKey;
      });

      const maxChildNumber = siblings.reduce((max, key) => {
        const section = sectionsDict[key];
        const orderingId = section?.section_ordering_id || `${parentOrderingId}.0`;
        const parts = orderingId.split('.');
        const childNum = parseInt(parts[parts.length - 1]);
        return Math.max(max, isNaN(childNum) ? 0 : childNum);
      }, 0);

      return `${parentOrderingId}.${maxChildNumber + 1}`;
    }
  };

  const findAllDescendants = (parentKey, sectionsDict, sectionOrder) => {
    const descendants = [];

    if (!sectionsDict[parentKey]) return descendants;

    const directChildren = sectionOrder.filter(key => {
      const section = sectionsDict[key];
      return section && section.parent_section_id === parentKey;
    });

    directChildren.forEach(childKey => {
      descendants.push(childKey);
      const grandChildren = findAllDescendants(childKey, sectionsDict, sectionOrder);
      descendants.push(...grandChildren);
    });

    return descendants;
  };

  const hasChildren = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    const hasChildSections = formData.sectionOrder.some(key => {
      const childSection = formData.sectionsDict[key];
      return childSection && childSection.parent_section_id === sectionKey;
    });

    const hasAgents = section.agentOrder && Array.isArray(section.agentOrder) && section.agentOrder.length > 0;

    return hasChildSections || hasAgents;
  };

  const hasAnyContent = (sectionKey) => {
    return hasChildren(sectionKey);
  };

  const toggleSectionExpansion = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const isSectionVisible = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section || !section.parent_section_id) return true;

    const parent = formData.sectionsDict[section.parent_section_id];
    if (!parent) return true;

    return expandedSections[section.parent_section_id] && isSectionVisible(section.parent_section_id);
  };

  const isAgentRunning = (agent) => {
    return agent.status === 'RUNNING';
  };

  const hasRunningAgents = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    const hasRunningDirectAgents = section.agentOrder.some(agentKey => {
      const agent = section.agentsDict[agentKey];
      return agent && isAgentRunning(agent);
    });

    if (hasRunningDirectAgents) return true;

    const childSections = formData.sectionOrder.filter(key => {
      const childSection = formData.sectionsDict[key];
      return childSection && childSection.parent_section_id === sectionKey;
    });

    return childSections.some(childKey => hasRunningAgents(childKey));
  };

  const handleRunConfirmation = (type) => {
    setSingleAgentExecution({ agentId: null, feedback: null });
    setRunType(type);
    setShowRunConfirmation(true);
  };

  const executeRunTemplate = async () => {
    setShowRunConfirmation(false);

    const isSelectiveRun = runType === 'selective_run';

    if (onProcessingStart) {
      onProcessingStart(
        runType === 'single_agent' ? 'Running agent' : 
        isSelectiveRun ? `Running ${selectedAgentIds.size} selected agents` :
        'Running template'
      );
    }

    try {
      if (hasPendingChanges) {
        await performSaveTemplate();
      }

      // Base payload config
      const payload = {
        template_id: templateId,
        fabric_id: fabricId,
        master_feedback: masterFeedback.trim() || null
      };

      // Handle specifics based on run type
      if (runType === 'single_agent') {
        payload.agent_id = singleAgentExecution.agentId || null;
        payload.feedback = singleAgentExecution.feedback || null;
        payload.enable_grounding_check =
          !!(singleAgentExecution.agentId && agentGrounding[singleAgentExecution.agentId]);
        payload.preserve_response = !!singleAgentExecution.preserve_response;
        payload.amend_response = !!singleAgentExecution.amend_response;
        payload.response_colour =
          (singleAgentExecution.preserve_response || singleAgentExecution.amend_response)
          ? (singleAgentExecution.response_colour || null)
          : null;
        payload.use_feedback_editor = !!singleAgentExecution.use_feedback_editor;
      } else if (isSelectiveRun) {
        // If all agents in template are selected, treat as full template run
        // (no agents array → backend routes to run_genbi_template)
        const allIds = getAllAgentIdsInTemplate();
        const isFullRun = selectedAgentIds.size === allIds.length &&
          allIds.every(id => selectedAgentIds.has(id));

        if (!isFullRun) {
          // Backend expects: agents: [{ agent_id, feedback, enable_grounding_check,
          // preserve_response, amend_response, response_colour, use_feedback_editor }]
          payload.agents = Array.from(selectedAgentIds).map(agentId => {
            const fb = agentFeedback[agentId];
            return {
              agent_id: agentId,
              feedback: fb && fb.trim() ? fb.trim() : null,
              enable_grounding_check: !!agentGrounding[agentId],
              ...getAgentPersistRunOptions(getAgentById(agentId))
            };
          });
        }
      } else {
        const hasAnyGrounding = Object.values(agentGrounding).some(Boolean);
        if (hasAnyGrounding) {
          const allAgents = [];
          (formData.sectionOrder || []).forEach(sKey => {
            const section = formData.sectionsDict?.[sKey];
            (section?.agentOrder || []).forEach(aKey => {
              const agent = section.agentsDict?.[aKey];
              if (agent?.id) {
                const fb = agentFeedback[agent.id];
                allAgents.push({
                  agent_id: agent.id,
                  feedback: fb && fb.trim() ? fb.trim() : null,
                  enable_grounding_check: !!agentGrounding[agent.id],
                  ...getAgentPersistRunOptions(agent)
                });
              }
            });
          });
          payload.agents = allAgents;
        }
      }

      const response = await axios.post(
        `${API_BASE_URL}/run-template`,
        payload,
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        onError(response.data.message || 'Failed to run template');
        return;
      }

      // --- Clear feedback ONLY for the agents that were executed ---
      if (isSelectiveRun) {
        setAgentFeedback(prev => {
          const newFeedback = { ...prev };
          Array.from(selectedAgentIds).forEach(agentId => delete newFeedback[agentId]);
          return newFeedback;
        });
        setAgentGrounding(prev => {
          const next = { ...prev };
          Array.from(selectedAgentIds).forEach(agentId => delete next[agentId]);
          return next;
        });
        clearSelectionAttachments(Array.from(selectedAgentIds));
        setSelectedAgentIds(new Set()); // clear selection after run
      } else if (runType === 'single_agent') {
        setAgentFeedback(prev => {
          const newFeedback = { ...prev };
          if (singleAgentExecution.agentId) {
            delete newFeedback[singleAgentExecution.agentId];
          }
          return newFeedback;
        });
        setAgentGrounding(prev => {
          if (!singleAgentExecution.agentId) return prev;
          const next = { ...prev };
          delete next[singleAgentExecution.agentId];
          return next;
        });
        if (singleAgentExecution.agentId) {
          clearSelectionAttachments(singleAgentExecution.agentId);
        }
        setSingleAgentExecution({ agentId: null, feedback: null });
      } else {
        // If it's a full template run, clear everything
        setAgentFeedback({});
        setAgentGrounding({});
        setAgentSelectedText({});
        setAgentSelectedContext({});
        setAgentPreviewOpen({});
        setSingleAgentExecution({ agentId: null, feedback: null });
      }

      setMasterFeedback('');

      await handleRefresh();
      if (onRefreshTemplateList) {
        onRefreshTemplateList();
      }

    } catch (error) {
      console.error('Error running template:', error);
      onError(error.message || 'Failed to run template');
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  const performSaveTemplate = async () => {
    if (!hasPendingChanges){
      // Still sync tab name even if no structural changes
      if (onTemplateNameSaved && formData.template_name) {
        onTemplateNameSaved(formData.template_name);
      }
      return
    }
    if (!formData.template_name.trim()) {
      throw new Error('Template name is required');
    }

    if (!formData.fabric_id) {
      throw new Error('Please select a data fabric');
    }

    if (formData.sectionOrder.length === 0) {
      throw new Error('At least one section is required');
    }

    const structuredSections = convertToStructuredSections(formData.sectionsDict, formData.sectionOrder);

    const editedResponses = [];
    formData.sectionOrder.forEach(sectionKey => {
      const section = formData.sectionsDict[sectionKey];
      if (!section) return;
      section.agentOrder.forEach(agentKey => {
        const agent = section.agentsDict[agentKey];
        if (agent && agent.id && agent.editedResponse) {
          editedResponses.push({
            agent_id: agent.id,
            markdown: agent.editedResponse.markdown,
            tables: agent.editedResponse.tables || [],
            graphs: agent.editedResponse.graphs || [],
            images: agent.editedResponse.images || [],
            in_place_images: agent.editedResponse.in_place_images || [],
            // Forward deletions so the backend can drop removed tables/figures.
            ...(agent.editedResponse.deleted_table_ids?.length && { deleted_table_ids: agent.editedResponse.deleted_table_ids }),
            ...(agent.editedResponse.deleted_graph_ids?.length && { deleted_graph_ids: agent.editedResponse.deleted_graph_ids }),
            ...(agent.editedResponse.deleted_image_ids?.length && { deleted_image_ids: agent.editedResponse.deleted_image_ids }),
            ...(agent.editedResponse.deleted_in_place_image_ids?.length && { deleted_in_place_image_ids: agent.editedResponse.deleted_in_place_image_ids }),
            ...(agent.editedResponse.deleted_in_place_graph_ids?.length && { deleted_in_place_graph_ids: agent.editedResponse.deleted_in_place_graph_ids })
          });
        }
      });
    });

    const cleanSections = {
      new: structuredSections.new.map(s => ({
        ...s,
        agents: s.agents.map(({ _editedResponse, ...rest }) => rest)
      })),
      modified: structuredSections.modified.map(s => ({
        ...s,
        agents: s.agents.map(({ _editedResponse, ...rest }) => rest)
      })),
      deleted: structuredSections.deleted
    };

    const requestData = {
      template_id: templateId,
      template_name: formData.template_name,
      fabric_id: formData.fabric_id,
      vm_config: formData.vm_config || false,
      lang_id: formData.lang_id || 1,
      sections: cleanSections,
      global_prompt: formData.global_prompt || null,
      format_json: formData.format_json || null,
      ...(editedResponses.length > 0 && { edited_responses: editedResponses })
    };

    const response = await axios.post(
      `${API_BASE_URL}/update-template`,
      requestData,
      { headers }
    );

    const result = response.data;

    if (result.status === 'NotAuthorized') {
      authNavigate('/login', 'iam', navigate);
      return;
    }

    if (result.status === 'failed') {
      throw new Error(result.message);
    }

    savedTemplateFingerprintRef.current = buildTemplateFingerprint(formData);
    setHasPendingChanges(false);

    // Update tab name in Redux
    if (onTemplateNameSaved && formData.template_name) {
      onTemplateNameSaved(formData.template_name);
    }
  };

  // Helper function for natural sorting of ordering IDs
  // S-prefix sections (summary) always sort before any numeric section
  const naturalCompare = (a, b) => {
    const aIsS = a.startsWith('S');
    const bIsS = b.startsWith('S');
    if (aIsS && !bIsS) return -1;
    if (!aIsS && bIsS) return 1;

    const aClean = aIsS ? a.slice(1) : a;
    const bClean = bIsS ? b.slice(1) : b;
    const aParts = aClean.split('.').map(num => parseInt(num, 10));
    const bParts = bClean.split('.').map(num => parseInt(num, 10));

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aNum = aParts[i] || 0;
      const bNum = bParts[i] || 0;

      if (aNum !== bNum) {
        return aNum - bNum;
      }
    }
    return 0;
  };

  const recalculateSiblingOrderingIds = (parentKey, sectionsDict, sectionOrder) => {
    if (!sectionsDict || typeof sectionsDict !== 'object' || !Array.isArray(sectionOrder)) {
      return sectionsDict;
    }

    if (parentKey && !sectionsDict[parentKey]) {
      console.warn(`Parent section ${parentKey} not found during recalculation`);
      return sectionsDict;
    }

    const siblings = sectionOrder.filter(key => {
      const section = sectionsDict[key];
      if (!section || section.isDeleted) return false;

      return (parentKey ? section.parent_section_id === parentKey : !section.parent_section_id);
    }).sort((a, b) => {
      const orderingA = sectionsDict[a]?.section_ordering_id || '0';
      const orderingB = sectionsDict[b]?.section_ordering_id || '0';
      return naturalCompare(orderingA, orderingB);
    });

    const updatedSectionsDict = { ...sectionsDict };

    // Determine if parent is a summary section — children of summary sections use S-prefix ordering
    const parentIsSummary = parentKey && sectionsDict[parentKey]?.is_summary_section;

    // Filter out root-level summary sections from numeric ordering (they have their own S-prefix system)
    // But if parent is a summary section, include its children (they get S-prefix sub-ordering)
    const numericSiblings = parentIsSummary
      ? siblings
      : siblings.filter(key => !sectionsDict[key]?.is_summary_section);

    numericSiblings.forEach((siblingKey, index) => {
      const section = updatedSectionsDict[siblingKey];

      if (!section) return;

      const newOrderingId = parentKey
        ? `${updatedSectionsDict[parentKey]?.section_ordering_id || '0'}.${index + 1}`
        : (() => {
    const fmt = formData.format_json?.numberingFormat;
    const startVal = (fmt === "none" || fmt === undefined) ? (formData.format_json?.numberingStartValue ?? 1) : 1;
    return String(startVal + index);
  })();

      if (section.section_ordering_id !== newOrderingId) {
        updatedSectionsDict[siblingKey] = {
          ...section,
          section_ordering_id: newOrderingId,
          isModified: section.id ? true : undefined
        };

        const children = sectionOrder.filter(key => {
          const childSection = sectionsDict[key];
          return childSection && !childSection.isDeleted && childSection.parent_section_id === siblingKey;
        });

        if (children.length > 0) {
          try {
            const childUpdates = recalculateSiblingOrderingIds(siblingKey, updatedSectionsDict, sectionOrder);
            if (childUpdates && typeof childUpdates === 'object') {
              Object.assign(updatedSectionsDict, childUpdates);
            }
          } catch (error) {
            console.error('Error updating children ordering:', error);
          }
        }
      }
    });

    return updatedSectionsDict;
  };

  const recalculateSummaryOrderingIds = (sectionsDict, sectionOrder) => {
    const updatedSectionsDict = { ...sectionsDict };

    const isRootSummary = (sec) => {
      if (!sec || !sec.is_summary_section || sec.isDeleted) return false;
      if (sec.parent_section_id) return false;
      const oid = sec.section_ordering_id || '';
      return !oid.includes('.');
    };

    const isChildSummary = (sec, parentKey) => {
      if (!sec || !sec.is_summary_section || sec.isDeleted) return false;
      if (sec.parent_section_id === parentKey) return true;
      const oid = sec.section_ordering_id || '';
      const parentOid = updatedSectionsDict[parentKey]?.section_ordering_id || '';
      return oid.startsWith(parentOid + '.') && oid.split('.').length === parentOid.split('.').length + 1;
    };

    const rootSummarySections = sectionOrder.filter(key => isRootSummary(updatedSectionsDict[key]))
      .sort((a, b) => {
        const orderingA = updatedSectionsDict[a]?.section_ordering_id || 'S0';
        const orderingB = updatedSectionsDict[b]?.section_ordering_id || 'S0';
        return naturalCompare(orderingA, orderingB);
      });

    rootSummarySections.forEach((key, index) => {
      const newOrderingId = `S${index + 1}`;
      const section = updatedSectionsDict[key];
      if (section.section_ordering_id !== newOrderingId) {
        updatedSectionsDict[key] = {
          ...section,
          section_ordering_id: newOrderingId,
          isModified: section.id ? true : undefined
        };
      }

      const children = sectionOrder.filter(childKey => isChildSummary(updatedSectionsDict[childKey], key))
        .sort((a, b) => {
          const orderingA = updatedSectionsDict[a]?.section_ordering_id || 'S0.0';
          const orderingB = updatedSectionsDict[b]?.section_ordering_id || 'S0.0';
          return naturalCompare(orderingA, orderingB);
        });

      children.forEach((childKey, childIndex) => {
        const childOrderingId = `${newOrderingId}.${childIndex + 1}`;
        const child = updatedSectionsDict[childKey];
        updatedSectionsDict[childKey] = {
          ...child,
          section_ordering_id: childOrderingId,
          parent_section_id: key,
          isModified: child.id ? true : undefined
        };
      });
    });

    return updatedSectionsDict;
  };

  const findInsertionPosition = (parentKey, sectionOrder, sectionsDict) => {
    if (!parentKey) {
      return sectionOrder.length;
    }

    const parentIndex = sectionOrder.indexOf(parentKey);
    if (parentIndex === -1) return sectionOrder.length;

    let insertIndex = parentIndex + 1;
    const parentSection = sectionsDict[parentKey];
    if (!parentSection) return sectionOrder.length;

    const parentOrderingId = parentSection.section_ordering_id;

    for (let i = parentIndex + 1; i < sectionOrder.length; i++) {
      const sectionKey = sectionOrder[i];
      const section = sectionsDict[sectionKey];
      if (!section) continue;

      const orderingId = section.section_ordering_id;

      if (orderingId && orderingId.startsWith(parentOrderingId + '.')) {
        insertIndex = i + 1;
      } else {
        break;
      }
    }

    return insertIndex;
  };

  const handleShowVersion = async (sectionKey, agentKey, agentId, version) => {
    onProcessingStart('Loading version...');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-agent-response-version`,
        { agent_id: agentId, version: version, fabric_id: fabricId },
        { headers }
      );

      if (response.data.status === 'success') {
        setFormData(prev => {
          const newSectionsDict = { ...prev.sectionsDict };
          const newAgentsDict = { ...newSectionsDict[sectionKey].agentsDict };
          
          newAgentsDict[agentKey] = {
            ...newAgentsDict[agentKey],
            response: response.data.data
          };

          newSectionsDict[sectionKey] = {
            ...newSectionsDict[sectionKey],
            agentsDict: newAgentsDict
          };

          return { ...prev, sectionsDict: newSectionsDict };
        });
        
        setSelectedVersions(prev => ({ ...prev, [agentId]: version }));
      } else {
        onError(response.data.message || 'Failed to load version');
      }
    } catch (err) {
      onError(err.message || 'An error occurred while fetching the version.');
    } finally {
      if (onProcessingEnd) onProcessingEnd();
    }
  };

  const handleCompareVersions = (agentId, vA, vB) => {
    if (vA === vB) return;
    const versionA = Math.min(vA, vB);
    const versionB = Math.max(vA, vB);
    setComparePanelState({ agentId, versionA, versionB });
  };

  const handleRestoreVersion = async (agentId, version) => {
    if (!window.confirm(`Are you sure you want to restore to version ${version}? This will delete all newer versions.`)) {
      return;
    }
    
    onProcessingStart('Restoring version...');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/restore-agent-version`,
        { agent_id: agentId, version: version },
        { headers }
      );

      if (response.data.status === 'success') {
        handleRefresh();
      } else {
        onError(response.data.message || 'Failed to restore version');
      }
    } catch (err) {
      onError(err.message || 'Failed to restore version');
    } finally {
      onProcessingEnd();
      setOpenVersionDropdown(null);
    }
  };

  const promptRestoreVersion = (agentId, version, versionName) => {
    if (templateData.running_agents > 0) {
      onError('Cannot restore version while agents are running. Please wait for all agents to complete.');
      return;
    }

    let agent = null;
    for (const sKey of Object.keys(formData.sectionsDict || {})) {
      for (const aKey of Object.keys(formData.sectionsDict[sKey]?.agentsDict || {})) {
        const a = formData.sectionsDict[sKey].agentsDict[aKey];
        if (a.id === agentId) { agent = a; break; }
      }
      if (agent) break;
    }

    const currentDocIds = (agent?.document_mappings || []).filter(d => d.selected).map(d => d.doc_id);
    const versionObj = (agent?.versions || []).find(v => v.version === version);
    const versionDocIds = Array.isArray(versionObj?.last_run_doc_ids) ? versionObj.last_run_doc_ids : [];

    const cSet = new Set(currentDocIds);
    const vSet = new Set(versionDocIds);
    const docsChanged = versionDocIds.length > 0 && (
      currentDocIds.length !== versionDocIds.length
      || currentDocIds.some(id => !vSet.has(id))
      || versionDocIds.some(id => !cSet.has(id))
    );

    const docNameMap = {};
    const vDocNames = agent?.version_doc_names || {};
    Object.entries(vDocNames).forEach(([id, name]) => {
      if (name) docNameMap[Number(id)] = name;
    });
    (agent?.document_mappings || []).forEach(d => {
      if (d.doc_name) docNameMap[d.doc_id] = d.doc_name;
    });

    const latestVersion = (agent?.versions || [])[0]?.version;
    const currentVersionName = latestVersion != null ? `Version ${latestVersion}` : 'Current';

    setRestoreVersionInfo({
      isOpen: true, agentId, version, versionName, fabricId,
      docsChanged, restoreDocs: docsChanged, versionDocIds, currentDocIds, docNameMap, currentVersionName
    });
  };

  const executeRestoreVersion = async () => {
    const { agentId, version, fabricId, restoreDocs } = restoreVersionInfo;
    setRestoreVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '', docsChanged: false, restoreDocs: false, versionDocIds: [], currentDocIds: [], docNameMap: {} });
    if (onProcessingStart) onProcessingStart('Restoring version...');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/restore-agent-version`,
        { agent_id: agentId, version: version, fabric_id: fabricId, restore_docs: restoreDocs },
        { headers }
      );

      if (response.data.status === 'success') {
        setSelectedVersions(prev => {
          const newState = { ...prev };
          delete newState[agentId];
          return newState;
        });
        setAgentFeedback(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentQuickEdit(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentsWithComments(prev => { const next = new Set(prev); next.delete(agentId); return next; });
        setAgentSelectedText(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentSelectedContext(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentPreviewOpen(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        handleRefresh();
      } else {
        onError(response.data.message || 'Failed to restore version.');
      }
    } catch (err) {
      onError(err.message || 'An error occurred while restoring the version.');
    } finally {
      if (onProcessingEnd) onProcessingEnd();
    }
  };

  // ── Prompt version: show (with warning intercept) ──
  const handleShowPromptVersion = (agentId, version, latestVersion) => {
    _doShowPromptVersion(agentId, version, version === latestVersion);
  };

  const _doShowPromptVersion = async (agentId, version, isLatest = false) => {
    onProcessingStart('Loading prompt version...');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-agent-prompt-version`,
        { agent_id: agentId, version: version, fabric_id: fabricId },
        { headers }
      );
      if (response.data.status === 'success') {
        const { agent_context, additional_context, context_lang_id } = response.data.data;
        setFormData(prev => {
          const newSectionsDict = { ...prev.sectionsDict };
          for (const sKey of Object.keys(newSectionsDict)) {
            const agentsDict = newSectionsDict[sKey].agentsDict;
            if (agentsDict) {
              for (const aKey of Object.keys(agentsDict)) {
                if (agentsDict[aKey].id === agentId) {
                  newSectionsDict[sKey] = {
                    ...newSectionsDict[sKey],
                    agentsDict: {
                      ...agentsDict,
                      [aKey]: {
                        ...agentsDict[aKey],
                        agent_context,
                        additional_context,
                        context_lang_id
                      }
                    }
                  };
                }
              }
            }
          }
          return { ...prev, sectionsDict: newSectionsDict };
        });
        setSelectedPromptVersions(prev => ({ ...prev, [agentId]: version }));
        if (isLatest) {
          setPromptVersionPreviewAgents(prev => {
            const newSet = new Set(prev);
            newSet.delete(agentId);
            return newSet;
          });
        } else {
          setPromptVersionPreviewAgents(prev => new Set([...prev, agentId]));
        }
      } else {
        onError(response.data.message || 'Failed to load prompt version');
      }
    } catch (err) {
      onError(err.message || 'An error occurred while fetching the prompt version.');
    } finally {
      if (onProcessingEnd) onProcessingEnd();
      setOpenPromptVersionDropdown(null);
    }
  };

  // ── Prompt version: restore ──
  const promptRestorePromptVersion = (agentId, version, versionName) => {
    if (templateData.running_agents > 0) {
      onError('Cannot restore prompt version while agents are running. Please wait for all agents to complete.');
      return;
    }
    setRestorePromptVersionInfo({ isOpen: true, agentId, version, versionName, fabricId });
  };

  const executeRestorePromptVersion = async () => {
    const { agentId, version, fabricId } = restorePromptVersionInfo;
    setRestorePromptVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '' });
    if (onProcessingStart) onProcessingStart('Restoring prompt version...');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/restore-agent-prompt-version`,
        { agent_id: agentId, version: version, fabric_id: fabricId },
        { headers }
      );
      if (response.data.status === 'success') {
        setAgentQuickEdit(prev => ({ ...prev, [agentId]: false }));
        setSelectedPromptVersions(prev => {
          const newState = { ...prev };
          delete newState[agentId];
          return newState;
        });
        setPromptVersionPreviewAgents(prev => {
          const newSet = new Set(prev);
          newSet.delete(agentId);
          return newSet;
        });
        handleRefresh();
      } else {
        onError(response.data.message || 'Failed to restore prompt version.');
      }
    } catch (err) {
      onError(err.message || 'An error occurred while restoring the prompt version.');
    } finally {
      if (onProcessingEnd) onProcessingEnd();
    }
  };

  const openConfigureModal = (sectionKey, agentKey) => {
    setCurrentAgentConfig({ sectionKey, agentKey });
    setConfigureModalOpen(true);
  };

  const handleTranslateAgent = async (agentId, targetLangId) => {
    if (onProcessingStart) {
      onProcessingStart('Translating agent response...');
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/translate-agent-response`,
        {
          agent_id: agentId,
          fabric_id: fabricId,
          target_lang_id: targetLangId
        },
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }


      if (response.data.status === 'success') {
        handleRefresh();
      } else {
        onError(response.data.message || 'Failed to translate agent response');
      }
    } catch (err) {
      console.error('Error translating agent response:', err);
      onError(err.message || 'An error occurred while translating');
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  const handleTranslateAgentContext = async (sectionKey, agentKey, targetLangId) => {
    if (onProcessingStart) onProcessingStart('Translating agent prompt...');
    setTranslateDropdown(null);

    try {
      const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
      if (!agent || !agent.id) {
        onError('Agent not found');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/translate-agent-context`,
        {
          agent_id: agent.id,
          fabric_id: fabricId,
          target_lang_id: targetLangId
        },
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'success') {
        handleRefresh();
      } else {
        onError(response.data.message || 'Failed to translate agent context');
      }
    } catch (err) {
      console.error('Error translating agent context:', err);
      onError(err.message || 'An error occurred while translating');
    } finally {
      if (onProcessingEnd) onProcessingEnd();
    }
  };

  useEffect(() => {
    setExpandedSections(prev => {
      const newExpanded = { ...prev };
      formData.sectionOrder.forEach(sectionKey => {
        if (!(sectionKey in newExpanded)) {
          newExpanded[sectionKey] = false;
        }
      });
      return newExpanded;
    });
  }, [formData.sectionOrder]);

  useEffect(() => {
    if (templateDataCache[templateId]) {
      const cachedData = templateDataCache[templateId];
      let { sectionsDict, sectionOrder } = convertSectionsToDict(cachedData.sections || []);
      sectionsDict = recalculateSummaryOrderingIds(sectionsDict, sectionOrder);

      const nextFormData = {
        template_name: cachedData.template_name,
        fabric_id: cachedData.fabric_id,
        fabric_name: cachedData.fabric_name,
        vm_config: cachedData.vm_config,
        lang_id: cachedData.lang_id,
        sectionsDict,
        sectionOrder,
        global_prompt: cachedData.global_prompt || '',
        format_json: cachedData.format_json || null
      };

      savedTemplateFingerprintRef.current = buildTemplateFingerprint(nextFormData);
      setFormData(nextFormData);

      setTemplateData({
        agent_count: cachedData.agent_count || 0,
        completed_agents: cachedData.completed_agents || 0,
        running_agents: cachedData.running_agents || 0,
        failed_agents: cachedData.failed_agents || 0,
        pending_agents: cachedData.pending_agents || 0,
        status: cachedData.status || '',
        report_gen_status: cachedData.report_gen_status || null,
        context_mapping_status: cachedData.context_mapping_status || ''
      });
      setIsInitialLoading(false);
      setHasPendingChanges(false);
      
      if (cachedData.fabric_id) {
        loadFabricDocuments(cachedData.fabric_id);
      }
    } else {
      setIsInitialLoading(true);
      fetchTemplateDetails();
    }
  }, [templateId, refreshKey]);

  // Show/hide dashboard processing popup based on initial loading state
  // (Mirrors the Canvas tab viewer pattern — uses parent's popup, not a local one)
  useEffect(() => {
    if (isInitialLoading) {
      if (onProcessingStart) {
        onProcessingStart(`Loading ${formData.template_name || templateName || 'template'} details`);
      }
    } else {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  }, [isInitialLoading, templateName]);

  useEffect(() => {
    if (isUserEditRef.current) {
      const savedFingerprint = savedTemplateFingerprintRef.current;
      setHasPendingChanges(
        savedFingerprint === null || buildTemplateFingerprint(formData) !== savedFingerprint
      );
    }
    isUserEditRef.current = false;
  }, [formData.sectionsDict, formData.sectionOrder]);

  const onCloseGenerateReport = (closed_due_to) => {
    setIsGenReportModalVisible(false);
    if (closed_due_to === 'gen'){
      handleRefresh();
    }
  }

  const fetchTemplateDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-template-details`,
        {
          template_id: templateId,
          fabric_id: fabricId,
          include_agent_responses: true
        },
        { headers }
      );

      if (response.data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === 'failed') {
        setError(response.data.message || 'Failed to load template details');
        onError(response.data.message || 'Failed to load template details');
        return;
      }

      const data = response.data.data;
      let { sectionsDict, sectionOrder } = convertSectionsToDict(data.sections || []);
      sectionsDict = recalculateSummaryOrderingIds(sectionsDict, sectionOrder);

      const nextFormData = {
        template_name: data.template_name,
        fabric_id: data.fabric_id,
        fabric_name: data.fabric_name,
        vm_config: data.vm_config,
        lang_id: data.lang_id,
        sectionsDict,
        sectionOrder,
        global_prompt: data.global_prompt || '',
        format_json: data.format_json || null
      };

      savedTemplateFingerprintRef.current = buildTemplateFingerprint(nextFormData);
      setFormData(nextFormData);

      setTemplateData({
        agent_count: data.agent_count || 0,
        completed_agents: data.completed_agents || 0,
        running_agents: data.running_agents || 0,
        failed_agents: data.failed_agents || 0,
        pending_agents: data.pending_agents || 0,
        status: data.status || '',
        report_gen_status: data.report_gen_status,
        context_mapping_status: data.context_mapping_status || ''
      });

      // Sync tab name on load
      if (onTemplateNameSaved && data.template_name) {
        onTemplateNameSaved(data.template_name);
      }

      setTemplateDataCache(prev => ({
        ...prev,
        [templateId]: data
      }));

      if (data.fabric_id) {
        await loadFabricDocuments(data.fabric_id);
      }

      setHasPendingChanges(false);

      restoreCurrentState();

    } catch (error) {
      console.error('Error fetching template details:', error);
      const errorMessage = error.message || 'Failed to load template details';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };

  const loadFabricDocuments = async (fabricId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/fabric_unstructured_data_tree`,
        {
          fabric_id: fabricId,
          include_documents: true,
          page: 1,
          size: 25
        },
        { headers }
      );

      if (response.data.status === 'success') {
        setFabricTreeData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading fabric documents:', error);
    }
  };

  // Sync Docs: fetch diff from backend and open modal
  const handleSyncDocs = async () => {
    setSyncDocsLoading(true);
    setSyncDocsModalOpen(true);
    setSyncDocsChanges([]);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/reconfigure-doc-mappings`,
        { template_id: templateId, fabric_id: fabricId },
        { headers }
      );
      if (response.data.status === 'success') {
        setSyncDocsChanges(response.data.changes || []);
      }
    } catch (err) {
      console.error('Error fetching doc sync diff:', err);
    } finally {
      setSyncDocsLoading(false);
    }
  };

  // Apply approved sync changes to formData
  const handleApplySyncChanges = (approvedChanges) => {
    // Build a lookup: agent_id -> change
    const changeMap = {};
    for (const change of approvedChanges) {
      changeMap[change.agent_id] = change;
    }

    setFormData(prev => {
      const newSectionsDict = {};

      for (const sectionKey of Object.keys(prev.sectionsDict)) {
        const section = prev.sectionsDict[sectionKey];
        const newAgentsDict = {};
        let sectionChanged = false;

        for (const agentKey of Object.keys(section.agentsDict || {})) {
          const agent = section.agentsDict[agentKey];
          const change = changeMap[agent.id];

          if (!change) {
            newAgentsDict[agentKey] = agent;
            continue;
          }

          // Build new mappings array immutably
          const existingMappings = agent.document_mappings || [];
          const currentDocIds = new Set(existingMappings.filter(d => d.selected).map(d => d.doc_id));
          const removedIds = new Set(change.removed_docs.map(d => d.doc_id));

          // Keep non-removed existing mappings
          const kept = existingMappings.filter(m => !removedIds.has(m.doc_id));

          // Add new docs that don't already exist
          const toAdd = change.added_docs
            .filter(doc => !currentDocIds.has(doc.doc_id))
            .map(doc => ({ doc_id: doc.doc_id, doc_name: doc.doc_name, selected: true }));

          newAgentsDict[agentKey] = {
            ...agent,
            document_mappings: [...kept, ...toAdd],
            isModified: true
          };
          sectionChanged = true;
        }

        newSectionsDict[sectionKey] = sectionChanged
          ? { ...section, agentsDict: newAgentsDict }
          : section;
      }

      return { ...prev, sectionsDict: newSectionsDict };
    });

    setSyncDocsModalOpen(false);
    isUserEditRef.current = true;
  };

  const handleRefresh = () => {
    saveCurrentState();
    setSelectedVersions({});
    setSelectedPromptVersions({});
    setPromptVersionPreviewAgents(new Set());
    setSelectedAgentIds(new Set()); // Reset selections on refresh

    setTemplateDataCache(prev => {
      const newCache = { ...prev };
      delete newCache[templateId];
      return newCache;
    });
    setRefreshKey(prev => prev + 1);
  };

  const addSection = (parentKey = null) => {
    isUserEditRef.current = true;
    const newKey = `temp_${Date.now()}_${Math.random()}`;
    const newOrderingId = generateOrderingId(parentKey, formData.sectionsDict, formData.sectionOrder);
    const insertPosition = findInsertionPosition(parentKey, formData.sectionOrder, formData.sectionsDict);

    const parentSection = parentKey ? formData.sectionsDict[parentKey] : null;
    const inheritSummary = parentSection?.is_summary_section || false;

    const newSection = {
      temp_id: newKey,
      section_name: '',
      parent_section_id: parentKey,
      section_ordering_id: newOrderingId,
      is_summary_section: inheritSummary,
      autogen_after: null,
      agentsDict: {},
      agentOrder: [],
      isNew: true
    };

    setFormData(prev => {
      const newSectionOrder = [...prev.sectionOrder];
      newSectionOrder.splice(insertPosition, 0, newKey);

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [newKey]: newSection
        },
        sectionOrder: newSectionOrder
      };
    });

    if (parentKey) {
      setExpandedSections(prev => ({
        ...prev,
        [parentKey]: true
      }));
    }
  };

  const addSummarySection = () => {
    isUserEditRef.current = true;
    const newKey = `temp_${Date.now()}_${Math.random()}`;

    setFormData(prev => {
      const rootSummarySections = prev.sectionOrder.filter(key => {
        const sec = prev.sectionsDict[key];
        return sec?.is_summary_section && !sec.parent_section_id;
      });
      const newOrderingId = `S${rootSummarySections.length + 1}`;

      const newSection = {
        temp_id: newKey,
        section_name: 'Special Section',
        parent_section_id: null,
        section_ordering_id: newOrderingId,
        is_summary_section: true,
        agentsDict: {},
        agentOrder: [],
        isNew: true
      };

      // Insert after the last S-series section (and its children), before regular sections
      let insertIndex = 0;
      for (let i = 0; i < prev.sectionOrder.length; i++) {
        const sec = prev.sectionsDict[prev.sectionOrder[i]];
        if (sec?.is_summary_section) {
          insertIndex = i + 1;
        } else {
          break;
        }
      }

      const newSectionOrder = [...prev.sectionOrder];
      newSectionOrder.splice(insertIndex, 0, newKey);

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [newKey]: newSection
        },
        sectionOrder: newSectionOrder
      };
    });
  };

  const showGenReportModal = () => {
    setIsGenReportModalVisible(true)
  }

  const addAgent = (sectionKey) => {
    isUserEditRef.current = true;
    const section = formData.sectionsDict[sectionKey];
    if (!section) return;

    const newAgentKey = `agent_temp_${Date.now()}_${Math.random()}`;
    const maxOrderingId = section.agentOrder.length > 0
      ? Math.max(...section.agentOrder.map(key =>
        section.agentsDict[key]?.agent_ordering_id || 0
      ))
      : 0;

    const allDocIds = [];
    fabricTreeData.forEach(fabric => {
      fabric.threads?.forEach(thread => {
        thread.documents?.forEach(doc => {
          allDocIds.push(doc.id);
        });
      });
    });

    const newAgent = {
      temp_id: newAgentKey,
      agent_name: '',
      agent_context: '',
      additional_context: null,
      agent_ordering_id: maxOrderingId + 1,
      document_mappings: allDocIds.map(docId => ({
        doc_id: docId,
        selected: true
      })),
      par_style: null,
      preserve_response: false,
      amend_response: false,
      response_colour: null,
      rich_formatting: false,
      table_structure_id: null,
      tag: null,
      isNew: true
    };

    setFormData(prev => ({
      ...prev,
      sectionsDict: {
        ...prev.sectionsDict,
        [sectionKey]: {
          ...prev.sectionsDict[sectionKey],
          agentsDict: {
            ...prev.sectionsDict[sectionKey].agentsDict,
            [newAgentKey]: newAgent
          },
          agentOrder: [...prev.sectionsDict[sectionKey].agentOrder, newAgentKey],
          hasModifiedAgents: true
        }
      }
    }));
    // Auto-expand section so new agent is immediately visible
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: true
    }));
  };

  const deleteSection = (sectionKey) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      let updatedSectionsDict = { ...prev.sectionsDict };
      let updatedSectionOrder = [...prev.sectionOrder];

      const allDescendants = findAllDescendants(sectionKey, updatedSectionsDict, updatedSectionOrder);

      const sectionsToRemove = [sectionKey, ...allDescendants];
      sectionsToRemove.forEach(keyToRemove => {
        const sectionToDelete = updatedSectionsDict[keyToRemove];
        if (sectionToDelete) {
          if (sectionToDelete.id) {
            updatedSectionsDict[keyToRemove] = {
              ...sectionToDelete,
              isDeleted: true
            };
          } else {
            delete updatedSectionsDict[keyToRemove];
          }
        }
      });

      updatedSectionOrder = updatedSectionOrder.filter(key =>
        !sectionsToRemove.includes(key)
      );

      const parentKey = section.parent_section_id;
      try {
        updatedSectionsDict = recalculateSiblingOrderingIds(parentKey, updatedSectionsDict, updatedSectionOrder);
      } catch (error) {
        console.error('Error recalculating sibling ordering:', error);
      }

      if (section.is_summary_section) {
        try {
          updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
        } catch (error) {
          console.error('Error recalculating summary ordering:', error);
        }
      }

      return {
        ...prev,
        sectionsDict: updatedSectionsDict,
        sectionOrder: updatedSectionOrder
      };
    });
  };

  const deleteAgent = (sectionKey, agentKey) => {
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      const agent = section.agentsDict[agentKey];
      if (!agent || isAgentRunning(agent)) return prev;

      isUserEditRef.current = true;

      let updatedAgentsDict = { ...section.agentsDict };
      let updatedAgentOrder = [...section.agentOrder];

      if (agent.id) {
        updatedAgentsDict[agentKey] = {
          ...agent,
          isDeleted: true
        };
      } else {
        delete updatedAgentsDict[agentKey];
      }

      updatedAgentOrder = updatedAgentOrder.filter(key => key !== agentKey);

      updatedAgentOrder.forEach((agentKey, index) => {
        const agent = updatedAgentsDict[agentKey];
        if (agent && agent.agent_ordering_id !== index + 1) {
          updatedAgentsDict[agentKey] = {
            ...agent,
            agent_ordering_id: index + 1,
            isModified: agent.id ? true : undefined
          };
        }
      });

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [sectionKey]: {
            ...section,
            agentsDict: updatedAgentsDict,
            agentOrder: updatedAgentOrder,
            hasModifiedAgents: true
          }
        }
      };
    });
  };

  const updateSectionField = (sectionKey, field, value) => {
    isUserEditRef.current = true;
    setFormData(prev => ({
      ...prev,
      sectionsDict: {
        ...prev.sectionsDict,
        [sectionKey]: {
          ...prev.sectionsDict[sectionKey],
          [field]: value,
          isModified: prev.sectionsDict[sectionKey].id ? true : undefined
        }
      }
    }));
  };

  const updateSpecialSectionConfig = (sectionKey, configKey, value) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      const currentConfig = section.special_section_config || {};
      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [sectionKey]: {
            ...section,
            special_section_config: { ...currentConfig, [configKey]: value },
            isModified: section.id ? true : undefined
          }
        }
      };
    });
  };

  const updateAgentField = (sectionKey, agentKey, field, value) => {
    isUserEditRef.current = true;

    // if user edits prompt fields manually, exit preview mode for that agent
    if (field === 'agent_context' || field === 'additional_context') {
      const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
      if (agentId) {
        setAgentQuickEdit(prev => ({ ...prev, [agentId]: false }));
        setPromptVersionPreviewAgents(prev => {
          const newSet = new Set(prev);
          newSet.delete(agentId);
          return newSet;
        });
      }
    }

    if (field === 'document_mappings') {
      const agentId = formData.sectionsDict[sectionKey]?.agentsDict[agentKey]?.id;
      if (agentId) {
        setAgentFeedback(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentQuickEdit(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentsWithComments(prev => { const next = new Set(prev); next.delete(agentId); return next; });
        setAgentSelectedText(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentSelectedContext(prev => { const next = { ...prev }; delete next[agentId]; return next; });
        setAgentPreviewOpen(prev => { const next = { ...prev }; delete next[agentId]; return next; });
      }
    }

    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      const agent = section.agentsDict[agentKey];
      const isPromptField = field === 'agent_context' || field === 'additional_context';
      const nextAgentContext = field === 'agent_context' ? value : (agent.agent_context || '');
      const nextAdditionalContext = field === 'additional_context' ? value : (agent.additional_context || '');
      const promptModified = isPromptField
        ? nextAgentContext !== (agent._savedAgentContext || '') ||
          nextAdditionalContext !== (agent._savedAdditionalContext || '')
        : !!agent._promptModified;
      const modifiedOutsidePrompt = !!agent._modifiedOutsidePrompt ||
        (agent.isModified === true && !agent._promptModified);
      const nextModifiedOutsidePrompt = isPromptField ? modifiedOutsidePrompt : true;

      return {
        ...prev,
        sectionsDict: {
          ...prev.sectionsDict,
          [sectionKey]: {
            ...section,
            agentsDict: {
              ...section.agentsDict,
              [agentKey]: {
                ...agent,
                [field]: value,
                _promptModified: promptModified,
                _modifiedOutsidePrompt: nextModifiedOutsidePrompt,
                isModified: agent.id
                  ? nextModifiedOutsidePrompt || promptModified
                  : undefined
              }
            },
            hasModifiedAgents: true
          }
        }
      };
    });
  };

  const openDocumentSelector = (sectionKey, agentKey) => {
    setCurrentAgentIndex({ sectionKey, agentKey });
    setDocumentSelectorOpen(true);
  };

  const handleDocumentSelection = (selectedDocIds) => {
    const { sectionKey, agentKey } = currentAgentIndex;
    const documentMappings = selectedDocIds.map(docId => ({
      doc_id: docId,
      selected: true
    }));

    updateAgentField(sectionKey, agentKey, 'document_mappings', documentMappings);
    setDocumentSelectorOpen(false);
  };

  const moveSectionOrder = (sectionKey, direction) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      const sectionIsSummary = section.is_summary_section;
      const parentKey = section.parent_section_id;

      // Get siblings at same level — same type only (S-series with S-series, ordinary with ordinary)
      const siblings = prev.sectionOrder.filter(key => {
        const siblingSection = prev.sectionsDict[key];
        if (!siblingSection) return false;
        if (!!siblingSection.is_summary_section !== !!sectionIsSummary) return false;
        if (parentKey) return siblingSection.parent_section_id === parentKey;
        return !siblingSection.parent_section_id;
      }).sort((a, b) => {
        const orderingA = prev.sectionsDict[a]?.section_ordering_id || '0';
        const orderingB = prev.sectionsDict[b]?.section_ordering_id || '0';
        return naturalCompare(orderingA, orderingB);
      });

      const currentSiblingIndex = siblings.indexOf(sectionKey);
      const targetSiblingIndex = direction === 'up' ? currentSiblingIndex - 1 : currentSiblingIndex + 1;

      if (targetSiblingIndex < 0 || targetSiblingIndex >= siblings.length) {
        return prev;
      }

      const targetSectionKey = siblings[targetSiblingIndex];

      const currentOrderingId = prev.sectionsDict[sectionKey].section_ordering_id;
      const targetOrderingId = prev.sectionsDict[targetSectionKey].section_ordering_id;

      let updatedSectionsDict = { ...prev.sectionsDict };

      updatedSectionsDict[sectionKey] = {
        ...updatedSectionsDict[sectionKey],
        section_ordering_id: targetOrderingId,
        isModified: updatedSectionsDict[sectionKey].id ? true : undefined
      };

      updatedSectionsDict[targetSectionKey] = {
        ...updatedSectionsDict[targetSectionKey],
        section_ordering_id: currentOrderingId,
        isModified: updatedSectionsDict[targetSectionKey].id ? true : undefined
      };

      const currentDescendants = findAllDescendants(sectionKey, updatedSectionsDict, prev.sectionOrder);
      const targetDescendants = findAllDescendants(targetSectionKey, updatedSectionsDict, prev.sectionOrder);

      currentDescendants.forEach(childKey => {
        const child = updatedSectionsDict[childKey];
        if (child && child.section_ordering_id) {
          const childParts = child.section_ordering_id.split('.');
          childParts[childParts.length - (childParts.length - currentOrderingId.split('.').length)] = targetOrderingId.split('.').pop();
          const newChildOrderingId = child.section_ordering_id.replace(currentOrderingId, targetOrderingId);
          updatedSectionsDict[childKey] = {
            ...child,
            section_ordering_id: newChildOrderingId,
            isModified: child.id ? true : undefined
          };
        }
      });

      targetDescendants.forEach(childKey => {
        const child = updatedSectionsDict[childKey];
        if (child && child.section_ordering_id) {
          const newChildOrderingId = child.section_ordering_id.replace(targetOrderingId, currentOrderingId);
          updatedSectionsDict[childKey] = {
            ...child,
            section_ordering_id: newChildOrderingId,
            isModified: child.id ? true : undefined
          };
        }
      });

      return {
        ...prev,
        sectionsDict: updatedSectionsDict
      };
    });
  };

  const moveAgentOrder = (sectionKey, agentKey, direction) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const section = prev.sectionsDict[sectionKey];
      if (!section) return prev;

      const currentIndex = section.agentOrder.indexOf(agentKey);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex >= 0 && targetIndex < section.agentOrder.length) {
        const newAgentOrder = [...section.agentOrder];
        [newAgentOrder[currentIndex], newAgentOrder[targetIndex]] =
          [newAgentOrder[targetIndex], newAgentOrder[currentIndex]];

        const updatedAgentsDict = { ...section.agentsDict };
        newAgentOrder.forEach((agentKey, index) => {
          const agent = updatedAgentsDict[agentKey];
          if (agent) {
            updatedAgentsDict[agentKey] = {
              ...agent,
              agent_ordering_id: index + 1,
              isModified: agent.id ? true : undefined
            };
          }
        });

        return {
          ...prev,
          sectionsDict: {
            ...prev.sectionsDict,
            [sectionKey]: {
              ...section,
              agentsDict: updatedAgentsDict,
              agentOrder: newAgentOrder,
              hasModifiedAgents: true
            }
          }
        };
      }

      return prev;
    });
  };

  const canMoveSectionUp = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    const sectionIsSummary = section.is_summary_section;
    const parentKey = section.parent_section_id;
    const siblings = formData.sectionOrder.filter(key => {
      const siblingSection = formData.sectionsDict[key];
      if (!siblingSection) return false;
      if (!!siblingSection.is_summary_section !== !!sectionIsSummary) return false;
      return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
    }).sort((a, b) => {
      const orderingA = formData.sectionsDict[a]?.section_ordering_id || '0';
      const orderingB = formData.sectionsDict[b]?.section_ordering_id || '0';
      return naturalCompare(orderingA, orderingB);
    });

    return siblings.indexOf(sectionKey) > 0;
  };

  const canMoveSectionDown = (sectionKey) => {
    const section = formData.sectionsDict[sectionKey];
    if (!section) return false;

    const sectionIsSummary = section.is_summary_section;
    const parentKey = section.parent_section_id;
    const siblings = formData.sectionOrder.filter(key => {
      const siblingSection = formData.sectionsDict[key];
      if (!siblingSection) return false;
      if (!!siblingSection.is_summary_section !== !!sectionIsSummary) return false;
      return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
    }).sort((a, b) => {
      const orderingA = formData.sectionsDict[a]?.section_ordering_id || '0';
      const orderingB = formData.sectionsDict[b]?.section_ordering_id || '0';
      return naturalCompare(orderingA, orderingB);
    });

    return siblings.indexOf(sectionKey) < siblings.length - 1;
  };

  const handleSaveAgentResponse = (agentId, editedData) => {
    isUserEditRef.current = true;
    setFormData(prev => {
      const newSectionsDict = { ...prev.sectionsDict };
      for (const sKey of Object.keys(newSectionsDict)) {
        const agentsDict = newSectionsDict[sKey].agentsDict;
        if (!agentsDict) continue;
        for (const aKey of Object.keys(agentsDict)) {
          if (agentsDict[aKey].id === agentId) {
            const existingAgent = agentsDict[aKey];

            // Apply deletions to the local response too, so view mode drops the
            // figure/table immediately instead of waiting for a backend reload.
            const delTables = new Set(editedData.deleted_table_ids || []);
            const delGraphs = new Set(editedData.deleted_graph_ids || []);
            const delImages = new Set(editedData.deleted_image_ids || []);
            let nextResponse = existingAgent.response;
            if (nextResponse && (delTables.size || delGraphs.size || delImages.size)) {
              nextResponse = {
                ...nextResponse,
                ...(Array.isArray(nextResponse.tables) && {
                  tables: nextResponse.tables.filter(t => !delTables.has(t.table_id))
                }),
                ...(Array.isArray(nextResponse.graphs) && {
                  graphs: nextResponse.graphs.filter(g => !delGraphs.has(g.graph_id))
                }),
                ...(Array.isArray(nextResponse.images) && {
                  images: nextResponse.images.filter(im => !delImages.has(im.image_id))
                })
              };
            }

            newSectionsDict[sKey] = {
              ...newSectionsDict[sKey],
              agentsDict: {
                ...agentsDict,
                [aKey]: {
                  ...existingAgent,
                  response: nextResponse,
                  editedResponse: editedData,
                  isModified: true
                }
              },
              hasModifiedAgents: true
            };
            return { ...prev, sectionsDict: newSectionsDict };
          }
        }
      }
      return prev;
    });
  };

   const docIdToName = useMemo(() => {
    const map = {};
    (fabricTreeData || []).forEach(fabric => {
      (fabric.threads || []).forEach(thread => {
        (thread.documents || []).forEach(doc => {
          if (doc?.id != null && doc.document_name) map[doc.id] = doc.document_name;
        });
      });
    });
    return map;
  }, [fabricTreeData]);
  
  const referenceIndex = useMemo(() => {
    const idToNumber = {};            // id -> { kind: 'table'|'figure', number }
    const idToArtifact = {};          // id -> { kind: 'table'|'graph'|'image', data }
    const idToReferencingAgents = {}; // id -> [{ agentId, agentLabel }]

    const sectionOrder = formData?.sectionOrder || [];
    const sectionsDict = formData?.sectionsDict || {};

    const addRef = (id, agentId, agentLabel) => {
      if (!id) return;
      if (!idToReferencingAgents[id]) idToReferencingAgents[id] = [];
      if (!idToReferencingAgents[id].some(a => a.agentId === agentId)) {
        idToReferencingAgents[id].push({ agentId, agentLabel });
      }
    };

    sectionOrder.forEach(sk => {
      const sec = sectionsDict[sk];
      if (!sec || sec.isDeleted) return;
      (sec.agentOrder || []).forEach(ak => {
        const ag = sec.agentsDict?.[ak];
        if (!ag || ag.isDeleted) return;
        const resp = ag.response || {};
        const editedTablesById = (ag.editedResponse?.tables || []).reduce((acc, table) => {
          if (table?.table_id) acc[table.table_id] = table;
          return acc;
        }, {});
        const editedGraphsById = (ag.editedResponse?.graphs || []).reduce((acc, graph) => {
          if (graph?.graph_id) acc[graph.graph_id] = graph;
          return acc;
        }, {});
        const editedImagesById = (ag.editedResponse?.images || []).reduce((acc, image) => {
          if (image?.image_id) acc[image.image_id] = image;
          return acc;
        }, {});

        // id -> display number (figures + tables) from this agent's own outputs
        (resp.graphs || []).forEach((g, idx) => {
          if (!g.graph_id) return;
          const figureNumber = g.graph_number ?? (idx + 1);
          const override = editedGraphsById[g.graph_id] || {};
          idToNumber[g.graph_id] = { kind: 'figure', number: figureNumber };
          idToArtifact[g.graph_id] = {
            kind: 'graph',
            data: {
              ...g,
              ...('subtitle' in override && { subtitle: override.subtitle }),
              ...('title' in override && { title: override.title }),
              figureNumber,
            },
          };
        });
        (resp.images || []).forEach((im, idx) => {
          if (!im.image_id) return;
          const figureNumber = im.img_num ?? (idx + 1);
          const override = editedImagesById[im.image_id] || {};
          idToNumber[im.image_id] = { kind: 'figure', number: figureNumber };
          idToArtifact[im.image_id] = {
            kind: 'image',
            data: {
              ...im,
              ...('subtitle' in override && { subtitle: override.subtitle }),
              ...('footer' in override && { footer: override.footer }),
              figureNumber,
            },
          };
        });
        (resp.tables || []).forEach((t, idx) => {
          if (!t.table_id) return;
          const tableNumber = t.table_number ?? (idx + 1);
          const override = editedTablesById[t.table_id] || {};
          idToNumber[t.table_id] = { kind: 'table', number: tableNumber };
          idToArtifact[t.table_id] = {
            kind: 'table',
            data: {
              ...t,
              html: override.html ?? t.html,
              ...('footnote' in override && { footnote: override.footnote }),
              tableNumber,
            },
          };
        });

        // which agents reference which ids — scan EVERY place a reference can live:
        // prose markdown, table cell HTML, and figure subtitle/title/footer (references
        // inside tables/captions are never resolved by the backend, so they stay raw).
        const agentLabel = `Agent ${ag.agent_ordering_id ?? ag.id}`;
        const searchable = [
          ag.editedResponse?.markdown ?? resp.markdown_raw ?? resp.markdown ?? '',
          ...(resp.tables || []).flatMap(t => [t.html || '', t.footnote || '']),
          ...(resp.graphs || []).flatMap(g => [g.subtitle || '', g.title || '']),
          ...(resp.images || []).flatMap(im => [im.subtitle || '', im.footer || '']),
        ].join('\n');
        const matches = searchable.match(/\[(agent_[^\]]+)\]/g) || [];
        matches.forEach(m => addRef(m.slice(1, -1), ag.id, agentLabel));
      });
    });

    return { idToNumber, idToArtifact, idToReferencingAgents };
  }, [formData]);

  const handleSaveEdits = async () => {
    if (!formData.template_name.trim()) {
      onError('Template name is required');
      return;
    }

    if (!formData.fabric_id) {
      onError('Please select a data fabric');
      return;
    }

    if (formData.sectionOrder.length === 0) {
      onError('At least one section is required');
      return;
    }

    saveCurrentState();
    setIsLoading(true);

    if (onProcessingStart) {
      onProcessingStart('Saving template changes');
    }

    try {
      await performSaveTemplate();
      handleRefresh();
      console.log('Template updated successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      onError(error.message || 'Failed to save template');
    } finally {
      setIsLoading(false);
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  const getRunModalBodyContent = () => {
    const isSingleAgent = runType === 'single_agent';
    const isSelectiveRun = runType === 'selective_run';

    // Get agents that reference (depend on) the current agent
    const referencedByLabels = isSingleAgent && singleAgentExecution.agentId
      ? getReferencedByAgentLabels(singleAgentExecution.agentId)
      : [];

    return (
      <div>
        <>
            <div className="ev-alert-card-warning mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Important:</strong> This action will {
                isSelectiveRun ? `run ${selectedAgentIds.size} selected agent(s)` :
                isSingleAgent ? 'run this agent only' :
                  'run all agents'
              }.
            </div>
            {/*
            <div 
              className={`ev-card-3 ev-radius-small mb-3 overflow-hidden master-feedback-box${masterFeedback.trim() ? ' master-feedback-box--active' : ''}`}
            >
              <div 
                className="d-flex align-items-center justify-content-between ev-padding-xsmall cursor-pointer"
                onClick={() => document.getElementById('master-feedback-textarea')?.focus()}
              >
                <span className="ev-fs-12 ev-reg-p-dark">
                  <i className="bi bi-chat-square-text me-1"></i>
                  Master Feedback
                  <span className="ev-fs-11 ms-1 master-feedback-optional">(optional)</span>
                </span>
                {masterFeedback.trim() && (
                  <span className="badge ev-btn-xsmall ev-fs-10 master-feedback-active-badge">
                    Active
                  </span>
                )}
              </div>
              <textarea
                id="master-feedback-textarea"
                className="w-100 ev-fs-12 ev-reg-p-dark border-0 px-2 pb-2 master-feedback-textarea"
                rows={2}
                placeholder="Instructions for all agents — e.g., Use recent data only, keep paragraphs concise..."
                value={masterFeedback}
                onChange={(e) => setMasterFeedback(e.target.value)}
              />
            </div>
            */}
          </>
        <div className="mb-2">
          <strong>Template:</strong> {formData.template_name}
        </div>
        <div className="mb-2">
          <strong>Fabric:</strong> {formData.fabric_name}
        </div>
        <div>
          {isSelectiveRun ? (
             <>
               <strong>Action:</strong> Run {selectedAgentIds.size} selected agent(s)
             </>
          ) : isSingleAgent ? (
            <>
              <strong>Action:</strong> Run single agent only
              {singleAgentExecution.feedback && (
                <div className="ev-card-8 mt-2 ev-radius-small">
                  <strong className="ev-small-p-blue ev-fs-11">
                    <i className="bi bi-chat-square-text me-1"></i>Feedback
                  </strong>
                  <div className="scrollable-element vh-10 mt-1 ev-fs-11 ev-reg-p-dark">
                    {singleAgentExecution.feedback
                      .split(/(Regarding this text(?: from a [^:]+)?:|Change requested:)/g)
                      .map((part, i) =>
                        /^(Regarding this text(?: from a [^:]+)?:|Change requested:)$/.test(part)
                          ? <strong key={i}>{part}</strong>
                          : part
                      )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <strong>Action:</strong> Run all agents
            </>
          )}
        </div>
        {isSingleAgent && referencedByLabels.length > 0 && (
          <div className="ev-alert-card-warning mt-3 warning-list-scroll">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> The Table/Figure/Graph references of the following cross referred agents will get Unlinked, please re-execute them to link it.
            <ul className="mb-0 mt-1">
              {referencedByLabels.map((label, idx) => (
                <li key={idx}>{label}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Check if there are any agents with feedback
  const hasAgentsWithFeedback = () => {
    return Object.values(agentFeedback).some(feedback => feedback && feedback.trim());
  };

  const hasAnyQuickFeedbackAgent = () => {
    return Object.entries(agentFeedback).some(([agentId, feedback]) =>
      feedback && feedback.trim() && agentQuickEdit[agentId]
    );
  };

  const hasSelectedQuickFeedbackAgent = () => {
    return Array.from(selectedAgentIds).some(id => agentQuickEdit[id]);
  };

  // Get section highlight class
  const getSectionHighlightClass = (sectionKey) => {
    const hasDirectFeedback = sectionHasFeedback(sectionKey);
    const isParent = isParentOfFeedbackSection(sectionKey);

    if (hasDirectFeedback) {
      return 'section-with-feedback-direct';
    } else if (isParent) {
      return 'section-with-feedback-parent';
    }
    return '';
  };

  // Section Type — mirrors the backend's genbi_sections.autogen_after column.
  // '' (Autogen) is the default and may be used by any number of sections.
  // The other four are auto-populated at export time and may be claimed by
  // only one section per template.
  const AUTOGEN_SECTION_TYPES = [
    { value: '', label: 'Autogen' },
    { value: 'TABLE_OF_CONTENTS', label: 'Table of Contents' },
    { value: 'LIST_OF_FIGURES', label: 'List of Figures' },
    { value: 'LIST_OF_TABLES', label: 'List of Tables' },
    { value: 'ABBREVIATIONS', label: 'Abbreviations' },
  ];

  const AUTOGEN_TYPE_LABELS = {
    TABLE_OF_CONTENTS: 'Table of Contents',
    LIST_OF_FIGURES: 'List of Figures',
    LIST_OF_TABLES: 'List of Tables',
    ABBREVIATIONS: 'Abbreviations',
  };

  // Returns the sectionKey already holding this special type, if any.
  const getAutogenSectionKeyUsing = (autogenType) => {
    if (!autogenType) return null;
    return Object.keys(formData.sectionsDict).find((key) => {
      const sec = formData.sectionsDict[key];
      if (!sec) return false;
      const secLevel = (sec.section_ordering_id?.split('.').length || 1) - 1;
      return sec.autogen_after === autogenType;
    }) || null;
  };

  const renderSection = (sectionKey) => {
    // Only render if visible by filter AND expanded logic
    if (!isSectionVisibleUnderFilter(sectionKey) || !isSectionVisible(sectionKey)) return null;

    const section = formData.sectionsDict[sectionKey];
    if (!section) return null;

    const level = (section.section_ordering_id?.split('.').length || 1) - 1;
   const effectiveAutogenAfter = section.autogen_after || null;
    const indentPx = level * 10;

    const baseIndentWidth = 10;
    const indentWidthPercent = baseIndentWidth + (level * 0.75);
    const sectionWidthPercent = 100;

    const indentStyle = {
        marginLeft: `${indentPx}px`,
        width: `${indentWidthPercent}%`,
        flexShrink: 0,
        textAlign: "right"
    };

    const sectionHasChildren = hasChildren(sectionKey);
    const isExpanded = expandedSections[sectionKey];
    const sectionHasRunning = hasRunningAgents(sectionKey);
    const sectionHasContent = hasAnyContent(sectionKey);
    const highlightClass = getSectionHighlightClass(sectionKey);

    // Section checkbox state calculations based on current filter
    const visibleIdsInSection = getVisibleAgentIdsInSection(sectionKey);
    const isSectionChecked = visibleIdsInSection.length > 0 && visibleIdsInSection.every(id => selectedAgentIds.has(id));
    const isSectionIndeterminate = visibleIdsInSection.length > 0 && visibleIdsInSection.some(id => selectedAgentIds.has(id)) && !isSectionChecked;

    return (
      <div key={sectionKey}>
        <div className={`d-flex align-items-center mb-2 ${highlightClass}`}>
          <div className="d-flex justify-content-end" style={indentStyle}>
            <span className="ev-message-card2 me-1 ms-1 mt-2 template-section-order-card d-flex align-items-center" >
              <button
                type="button"
                className="btn p-0 me-1 ev-small-p-blue border-0 fs-11 bg-transparent"
                onClick={() => toggleSectionExpansion(sectionKey)}
                disabled={!hasAnyContent(sectionKey)}
              >
                <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
              </button>
              {/* Checkbox for Section */}
              <input 
                 type="checkbox" 
                 className="form-check-input  me-1 mb-1"
                 checked={isSectionChecked}
                 ref={el => { if(el) el.indeterminate = isSectionIndeterminate; }}
                 onChange={(e) => handleSectionCheckbox(sectionKey, e.target.checked)}
                 disabled={visibleIdsInSection.length === 0}
                 title="Select/Deselect visible agents in this section"
              />
              {section.is_summary_section
                ? <><i className="bi bi-journal-text me-1"></i>{section.section_ordering_id || 'S'}</>
                : section.section_ordering_id}
            </span>
          </div>
          <div className={`template-viewer-section-name-col ev-radius-xsmall mt-2`}>
            <input
  type="text"
  className={`formField2 ev-btn-small ev-fs-12 ev-radius-xsmall template-viewer-sec-name-input${section.section_name ? ' avatar-tooltip' : ''}`}
  placeholder="Section Name"
  value={section.section_name}
  title={section.section_name}
  data-tooltip={section.section_name || undefined}
  onChange={(e) => updateSectionField(sectionKey, 'section_name', e.target.value)}
/>
            <div className="text-end template-viewer-sec-buttons">
              {vmConfig && (
                <button
                  type="button"
                  className="ev-message-card2 ev-message-card5 ev-btn-xsmall ev-bold-reg ev-fs-11 ms-1"
                  onClick={() => openVMSelector(sectionKey)}
                >
                  <i className="bi bi-chat-dots-fill"></i> Link Value Messages ({getSelectedVMCount(sectionKey)})
                </button>
              )}
              {section.is_summary_section && (
                <div
                  ref={openSpecialSectionConfig === sectionKey ? specialSectionConfigRef : null}
                  className="position-relative d-inline-block ms-1"
                >
                  <button
                    type="button"
                    className="no-wrap ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg border-0 d-flex align-items-center"
                    onClick={() => setOpenSpecialSectionConfig(prev => prev === sectionKey ? null : sectionKey)}
                  >
                    <i className="bi bi-bounding-box me-1" />
                    Config
                    <i className="bi bi-chevron-down ms-1" />
                  </button>

                  {openSpecialSectionConfig === sectionKey && (() => {
                    const ssc = section.special_section_config || {};
                    return (
                      <div className="position-absolute top-100 start-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2" style={{ minWidth: '220px' }}>
                        <span className="ev-small-p-blue ev-bold-med px-2 pt-1 pb-1 text-start text-nowrap">Section Config</span>

                        {/* Outside Border */}
                        <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25">
                          <span className="d-flex align-items-center gap-2 text-nowrap">
                            <i className="bi bi-box-arrow-up-right" />
                            <span>Outside Border</span>
                          </span>
                          <div
                            className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${ssc.is_outside_border ? 'agent-lock-toggle--on' : ''}`}
                            onClick={() => updateSpecialSectionConfig(sectionKey, 'is_outside_border', !ssc.is_outside_border)}
                          >
                            <div className="agent-lock-toggle-knob" />
                          </div>
                        </div>

                        {/* Heading BG Color */}
                        <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-0 rounded-2 border border-secondary border-opacity-25" style={{ minHeight: 0, ...(ssc.is_outside_border && { opacity: 0.4, pointerEvents: 'none' }) }}>
                          <span className="d-flex align-items-center gap-2 text-nowrap">
                            <i className="bi bi-paint-bucket" />
                            <span>Heading BG</span>
                          </span>
                          <span className="d-flex align-items-center" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                          <ColorPickerButton
                            value={ssc.heading_bg_color || '#FFFFFF'}
                            onChange={(color) => updateSpecialSectionConfig(sectionKey, 'heading_bg_color', color)}
                            icon={<i className="bi bi-square-fill" />}
                          />
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {!section.is_summary_section && (
                <div
                  className="position-relative d-inline-block ms-1"
                  ref={openSectionTypeDropdown === sectionKey ? sectionTypeDropdownRef : null}
                >
                  <button
                    type="button"
                    className={`${effectiveAutogenAfter ? 'ev-message-card7' : 'ev-btn-blue-outline-round'} ev-btn-xsmall ev-bold-reg ev-fs-11 avatar-tooltip-bottom`}
                    onClick={() => toggleSectionTypeDropdown(sectionKey)}
                    data-tooltip="controls how this section is filled at export"
                  >
                    {AUTOGEN_TYPE_LABELS[effectiveAutogenAfter] || 'Autogen'}
                    <i className="bi bi-chevron-down ms-1 ev-fs-10"></i>
                  </button>
                  {openSectionTypeDropdown === sectionKey && (
                    <div className="position-absolute top-100 start-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2 section-type-dropdown-panel">
                      <span className="ev-small-p-blue ev-bold-med px-1 mb-1 text-start">Content Source</span>
                      {AUTOGEN_SECTION_TYPES.map((opt) => {
                        const usedByKey = opt.value ? getAutogenSectionKeyUsing(opt.value) : null;
                        const isTakenElsewhere = !!usedByKey && usedByKey !== sectionKey;
                        const usedByOrdering = isTakenElsewhere ? formData.sectionsDict[usedByKey]?.section_ordering_id : null;
                        const isCurrent = (effectiveAutogenAfter || '') === opt.value;
                        return (
                          <button
                            key={opt.value || 'ai-response'}
                            type="button"
                            className={`par-style-item d-flex align-items-center ev-fs-12 py-2 px-2 ${isCurrent ? 'active' : ''} ${isTakenElsewhere ? 'opacity-50 avatar-tooltip' : ''}`}
                            disabled={isTakenElsewhere}
                            data-tooltip={isTakenElsewhere ? `Already used in S.${usedByOrdering}` : ''}
                            onClick={() => {
                              updateSectionField(sectionKey, 'autogen_after', opt.value || null);
                              closeSectionTypeDropdown();
                            }}
                          >
                            <i className={`bi ${isCurrent ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg  ev-fs-11 ms-1"
                onClick={() => addSection(sectionKey)}
              >
                <i className="bi bi-plus-circle-fill"></i> Section
              </button>
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg ev-fs-11 ms-1"
                onClick={() => addAgent(sectionKey)}
              >
                <i className="bi bi-plus-circle-fill"></i> AI Agent
              </button>
              <button
                type="button"
                className={`ev-btn-red-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10 ms-1 me-1 avatar-tooltip-bottom`}
                onClick={() => deleteSection(sectionKey)}
                disabled={sectionHasRunning}
                data-tooltip={sectionHasRunning ? "Wait for my agents to complete" : "Delete section"}
              >
                <i className="bi bi-trash"></i>
              </button>           
            </div>
          </div>
          <div className="col-1">
            <div className="ev-message-card2 me-1 ms-2 template-caret-card margin-top-10" >
              <button
                type="button"
                className="template-ordering-btn"
                onClick={() => moveSectionOrder(sectionKey, 'up')}
                disabled={!canMoveSectionUp(sectionKey)}
              >
                <i className="bi bi-caret-up-square"></i>
              </button>
              <button
                type="button"
                className="template-ordering-btn ms-1"
                onClick={() => moveSectionOrder(sectionKey, 'down')}
                disabled={!canMoveSectionDown(sectionKey)}
              >
                <i className="bi bi-caret-down-square"></i>
              </button>
          </div>
        </div>
        </div>

        {isExpanded && section.agentOrder?.map((agentKey) => {
          const agent = section.agentsDict[agentKey];
          // Hide if not matching filter logic
          if (!agent || !isAgentVisible(agent)) return null;

          const agentIndex = section.agentOrder.indexOf(agentKey);
          const agentRunning = isAgentRunning(agent);
          const agentHasComments = hasComments(sectionKey, agentKey);
          const commentData = getCommentData(sectionKey, agentKey);
          const isLocked = agent.is_locked || false;

          // Docs changed since last successful run — feedback/Quick Edit
          // must be disabled so stale references don't leak into the
          // response via feedback loops. Generate (fresh run) stays enabled.
          const lastRunDocIds = Array.isArray(agent.last_run_doc_ids) ? agent.last_run_doc_ids : [];
          const currentDocIds = (agent.document_mappings || []).filter(d => d.selected).map(d => d.doc_id);
          const hasConfiguredDocs = currentDocIds.length > 0;
          const hasReferenceAgents = (agent.reference_agent_ids || []).length > 0;
          const hasPromptOrTable =
            !!(agent.agent_context && agent.agent_context.trim()) ||
            agent.table_structure_id != null;
          const missingKnowledgeSource =
            hasPromptOrTable && !hasConfiguredDocs && !hasReferenceAgents;
          const lastRunSet = new Set(lastRunDocIds);
          const currentSet = new Set(currentDocIds);
          const docsStale =
            agent.status === 'COMPLETED'
            && agent.last_run_doc_ids != null
            && (
              currentDocIds.length !== lastRunDocIds.length
              || currentDocIds.some(id => !lastRunSet.has(id))
              || lastRunDocIds.some(id => !currentSet.has(id))
            );

          // Manual/quick-feedback versions can be edited again only through
          // Quick feedback; Thinking stays a fresh generation path.
          const isSourceManual = isLatestManualResponse(agent);
          const isQuickFeedbackMode = !!agentQuickEdit[agent.id];
          const manualNeedsQuickFeedback = isSourceManual && !isQuickFeedbackMode;
          const feedbackBlocked = docsStale || manualNeedsQuickFeedback;
          const showFeedbackSwitch = !isLocked && !docsStale;
          const feedbackPlaceholder = isLocked
            ? 'Unlock agent to provide feedback'
            : (docsStale
                ? 'Documents changed — run a fresh generation first'
                : (manualNeedsQuickFeedback
                    ? 'Response was manually edited — either click run/generate for fresh generation or use quick feedback'
                    : (isQuickFeedbackMode
                        ? 'Describe the exact change (Required)'
                        : 'Refine past result — thorough (Optional)')));

          return (
            <div key={agentKey} data-agent-id={agent.id}>
              <div className="d-flex mt-2 mb-2">
                <div style={indentStyle}>
                  {/* Empty space for alignment */}
                </div>

                <div className="template-viewer-agent-content">
                  <div className="ev-card-8 ev-radius-small ev-padding-small" >
                    <div className="row margin-bottom-1">
                      <div className="col-6 pt-1 d-flex align-items-center">
                        {/* Agent Selection Checkbox */}
                        <input
                          type="checkbox"
                          className="form-check-input ms-1 me-2"
                          checked={agent.id ? selectedAgentIds.has(agent.id) : false}
                          onChange={() => agent.id && toggleAgentSelection(agent.id)}
                          disabled={!agent.id}
                          title={
                            !agent.id
                              ? "Save template first to select this agent"
                              : "Select this agent"
                          }
                        />
                        <span className={`ev-message-card3 ev-btn-small ev-fs-11 text-nowrap ${agent.tag ? 'avatar-tooltip' : ''}`}
                          data-tooltip={agent.tag ? `Tag: ${agent.tag}` : undefined}
                        >
                          <i className="bi bi-robot"></i> S.{section.section_ordering_id}. Agent {agent.agent_ordering_id} 
                        </span>

                        <div className="agent-version-selector-wrap">
                          <VersionSelector
                            versions={agent.versions}
                            selectedVersionNumber={selectedVersions[agent.id]}
                            onShow={(version, versionName) => handleShowVersion(sectionKey, agentKey, agent.id, version)}
                            onRestore={(version, versionName) => promptRestoreVersion(agent.id, version, versionName)}
                            onCompare={(vA, vB) => handleCompareVersions(agent.id, vA, vB)}
                            restoreDisabled={agentRunning || templateData.running_agents > 0}
                          />
                        </div>
                        
                      </div>

                      <div className="col-6 text-end d-flex align-items-center justify-content-end gap-1" >
                        {/* Agent Settings Dropdown — Lock · Rich Format · Extend · Amend */}
                        <div
                          ref={openAgentSettingsDropdown === agentKey ? settingsDropdownRef : null}
                          className="position-relative d-inline-block"
                        >
                          <button
                            type="button"
                            className="no-wrap ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg border-0 d-flex align-items-center"
                            onClick={() => setOpenAgentSettingsDropdown(prev => prev === agentKey ? null : agentKey)}
                          >
                            <i className={`bi ${isLocked ? 'bi-lock-fill' : 'bi-sliders'} me-1`} />
                            Settings
                            <i className="bi bi-chevron-down ms-1" />
                          </button>

                          {openAgentSettingsDropdown === agentKey && (
                            <div className="position-absolute top-100 start-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2 min-w-200">
                              <span className="ev-small-p-blue ev-bold-med px-2 pt-1 pb-1 text-start text-nowrap">Agent Settings</span>

                              {/* Lock */}
                              <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25">
                                <span className="d-flex align-items-center gap-2 text-nowrap">
                                  <i className={`bi ${isLocked ? 'bi-lock-fill' : 'bi-unlock'}`} />
                                  <span>Lock</span>
                                </span>
                                <div
                                  className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${isLocked ? 'agent-lock-toggle--on' : ''}`}
                                  onClick={() => updateAgentField(sectionKey, agentKey, 'is_locked', !isLocked)}
                                >
                                  <div className="agent-lock-toggle-knob" />
                                </div>
                              </div>

                              {/* Rich Formatting */}
                              <div className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked || !!agent.preserve_response || !!agent.amend_response ? 'opacity-50' : ''}`}>
                                <span className="d-flex align-items-center gap-2 text-nowrap">
                                  <i className="bi bi-stars" />
                                  <span>Rich Format</span>
                                </span>
                                <div
                                  className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${!!agent.rich_formatting ? 'agent-lock-toggle--on' : ''}`}
                                  onClick={() => { if (isLocked || !!agent.preserve_response || !!agent.amend_response) return; updateAgentField(sectionKey, agentKey, 'rich_formatting', !agent.rich_formatting); }}
                                >
                                  <div className="agent-lock-toggle-knob" />
                                </div>
                              </div>

                              {/* Extend — only when COMPLETED */}
                              {agent.status === 'COMPLETED' && (
                                <div className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked || !!agent.rich_formatting ? 'opacity-50' : ''}`}>
                                  <span className="d-flex align-items-center gap-2 text-nowrap">
                                    <i className="bi bi-layout-text-sidebar" />
                                    <span>Extend</span>
                                  </span>
                                  <div
                                    className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${!!agent.preserve_response ? 'agent-lock-toggle--on' : ''}`}
                                    onClick={() => {
                                      if (isLocked || !!agent.rich_formatting) return;
                                      const next = !agent.preserve_response;
                                      updateAgentField(sectionKey, agentKey, 'preserve_response', next);
                                      if (next) {
                                        updateAgentField(sectionKey, agentKey, 'amend_response', false);
                                      } else {
                                        updateAgentField(sectionKey, agentKey, 'response_colour', null);
                                      }
                                    }}
                                  >
                                    <div className="agent-lock-toggle-knob" />
                                  </div>
                                </div>
                              )}

                              {/* Amend — only when COMPLETED */}
                              {agent.status === 'COMPLETED' && (
                                <div className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked || !!agent.rich_formatting ? 'opacity-50' : ''}`}>
                                  <span className="d-flex align-items-center gap-2 text-nowrap">
                                    <i className="bi bi-pencil-square" />
                                    <span>Amend</span>
                                  </span>
                                  <div
                                    className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${!!agent.amend_response ? 'agent-lock-toggle--on' : ''}`}
                                    onClick={() => {
                                      if (isLocked || !!agent.rich_formatting) return;
                                      const next = !agent.amend_response;
                                      updateAgentField(sectionKey, agentKey, 'amend_response', next);
                                      if (next) {
                                        updateAgentField(sectionKey, agentKey, 'preserve_response', false);
                                      } else {
                                        updateAgentField(sectionKey, agentKey, 'response_colour', null);
                                      }
                                    }}
                                  >
                                    <div className="agent-lock-toggle-knob" />
                                  </div>
                                </div>
                              )}

                              {/* Color picker — when Extend or Amend is on */}
                              {agent.status === 'COMPLETED' && (!!agent.preserve_response || !!agent.amend_response) && (
                                <div className={`ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25 ${isLocked ? 'opacity-50' : ''}`}>
                                  <span className="d-flex align-items-center gap-2 text-nowrap">
                                    <i className="bi bi-palette" />
                                    <span>Text Color</span>
                                  </span>
                                  <span className="d-flex align-items-center" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                                    <ColorPickerButton
                                      value={agent.response_colour || '#000000'}
                                      onChange={(colour) => { if (isLocked) return; updateAgentField(sectionKey, agentKey, 'response_colour', colour); }}
                                      icon={<i className="bi bi-alphabet-uppercase" />}
                                    />
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>


                        <span className=""> <StatusBadgeSm status={agent.status} /></span>
                        
                        <button
                          type="button"
                          className="ev-btn-blue-fill-round  ev-bold-reg ev-btn-small avatar-tooltip text-nowrap"
                          onClick={() => openConfigureModal(sectionKey, agentKey)}
                          disabled={isLocked}
                          data-tooltip={isLocked ? 'Unlock agent to configure' : ''}
                        >
                          <i className ="bi bi-gear-wide"></i> Configure
                        </button>

                        <div className="position-relative d-inline-block">
                          <TableStructureSelector
                            sectionKey={sectionKey}
                            agentKey={agentKey}
                            selectedId={agent.table_structure_id}
                            onSelect={(id) => updateAgentField(sectionKey, agentKey, 'table_structure_id', id)}
                            disabled={isLocked}
                            structures={tableStructures}
                            loading={tableStructuresLoading}
                          />
                        </div>

                        <button
                          type="button"
                          className={`ev-btn-red-fill-round ev-fs-10 ev-radius-xsmall ev-icon-btn-padding-small avatar-tooltip`}
                          onClick={() => deleteAgent(sectionKey, agentKey)}
                          disabled={isLocked || agentRunning}
                          data-tooltip={agentRunning ? "Agent is in running state" : isLocked ? "Unlock agent to delete" : "Delete agent"}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      className="formField4 mb-2 ev-btn-reg"
                      placeholder="Name"
                      value={agent.agent_name}
                      onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_name', e.target.value)}
                    />
                    {/* <label className="ev-small-p-blue ev-bold-med">
                      &nbsp;Prompt
                    </label> */}
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-1">
                      <div className="d-flex align-items-center gap-1">
                        <div className="btn-group custom-btn-group-outer mb-1" role="group">
                          <button
                            type="button"
                            className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${!activePromptTab[agentKey] || activePromptTab[agentKey] === "prompt" ? "ev-btn-blue-fill-round" : "ev-btn-lightblue-fill-round"}`}
                            onClick={() =>
                              setActivePromptTab((prev) => ({
                                ...prev,
                                [agentKey]: "prompt",
                              }))
                            }
                          >
                            Prompt
                          </button>
                          <button
                            type="button"
                            className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${activePromptTab[agentKey] === "additional_context" ? "ev-btn-blue-fill-round" : "ev-btn-lightblue-fill-round"}`}
                            onClick={() =>
                              setActivePromptTab((prev) => ({
                                ...prev,
                                [agentKey]: "additional_context",
                              }))
                            }
                          >
                            Additional Context
                          </button>
                        </div>

                        {/* Prompt Version Dropdown — same structure as VersionSelector */}
                        {agent.prompt_versions && agent.prompt_versions.length > 0 && (
                          <PromptVersionSelector
                            promptVersions={agent.prompt_versions}
                            selectedPromptVersion={selectedPromptVersions[agent.id]}
                            onShow={(version) => handleShowPromptVersion(agent.id, version, agent.prompt_versions[0]?.version)}
                            onRestore={(version, versionName) => promptRestorePromptVersion(agent.id, version, versionName)}
                            isLocked={isLocked || agentRunning || templateData.running_agents > 0}
                          />
                        )}
                      </div>

                      <div className="position-relative">
                        <div className="ev-message-card1 ev-btn-xsmall d-flex align-items-center pe-1 ev-radius-large prompt-translate-pill">
                          <i className="bi bi-globe2 me-1"></i>
                          <span>{allLanguages.find(l => l.id === (agent.context_lang_id ?? 1))?.name ?? 'English'}</span>
                          {agent.status === 'COMPLETED' &&
                            (agent.response?.language_id ?? 1) !== (agent.context_lang_id ?? 1) && (
                            <span
                              className="ms-1 avatar-tooltip"
                              data-tooltip={`Response is in ${allLanguages.find(l => l.id === (agent.response?.language_id ?? 1))?.name ?? 'English'} — prompt & response languages are out of sync`}
                            >
                              <i className="bi bi-exclamation-triangle-fill ev-small-p-orange"></i>
                            </span>
                          )}
                          <button
                            type="button"
                            className="ev-btn-blue-fill-round ev-btn-xsmall ms-2 avatar-tooltip"
                            disabled={isLoading || isLocked || promptVersionPreviewAgents.has(agent.id)}
                            data-tooltip={isLocked ? 'Unlock agent to translate' : promptVersionPreviewAgents.has(agent.id) ? 'Restore prompt version to enable translate' : 'Translate prompt'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTranslateDropdown(
                                translateDropdown?.sectionKey === sectionKey && translateDropdown?.agentKey === agentKey
                                  ? null
                                  : { sectionKey, agentKey }
                              );
                            }}
                          >
                            {isLoading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-translate"></i> Translate</>}
                          </button>
                        </div>
                        {translateDropdown?.sectionKey === sectionKey && translateDropdown?.agentKey === agentKey && (
                          <AgentTranslationModal
                            currentLanguageId={agent.context_lang_id ?? 1}
                            onClose={() => setTranslateDropdown(null)}
                            onTranslate={(targetLangId) => handleTranslateAgentContext(sectionKey, agentKey, targetLangId)}
                            otherSideLangId={agent.response?.language_id ?? 1}
                            otherSideLabel="Response"
                          />
                        )}
                      </div>

                    </div>
                    <div className="mb-0">
                      {/* <textarea
                        className="formField3 ev-padding-xsmall ev-fs-12"
                        placeholder="Provide context here"
                        rows="4"
                        value={agent.agent_context || ''}
                        onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_context', e.target.value)}
                      /> */}
                      {(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? (
                        <textarea
                          className="formField3 ev-padding-xsmall ev-fs-12"
                          placeholder="Provide context here"
                          rows="4"
                          value={agent.agent_context || ''}
                          onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_context', e.target.value)}
                          disabled={isLocked || promptVersionPreviewAgents.has(agent.id)}
                        />
                      ) : (
                        <textarea
                          className="formField3 ev-padding-xsmall ev-fs-12"
                          placeholder="Provide additional context here (optional)"
                          rows="4"
                          value={agent.additional_context || ''}
                          onChange={(e) => updateAgentField(sectionKey, agentKey, 'additional_context', e.target.value)}
                          disabled={isLocked || promptVersionPreviewAgents.has(agent.id)}
                        />
                      )}
                    </div>

                    {/* Prompt preview blocked banner */}
                    {promptVersionPreviewAgents.has(agent.id) && (
                      <div className="d-flex align-items-center ev-card-10 ev-radius-xsmall px-2 py-1 mt-1">
                        <i className="bi bi-lock-fill ev-small-p-blue me-1"></i>
                        <span className="ev-fs-10 ev-small-p-dark">
                          Viewing an old version — editing is disabled. Restore to make it active.
                        </span>
                      </div>
                    )}

                    <div className="mt-1">
                      {agent.status === 'COMPLETED' && (
                        <div className="mb-2">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <div className="d-flex align-items-center gap-2">
                              <label className="ev-small-p-blue ev-bold-med mb-0">
                                &nbsp;Feedback
                              </label>
                              {showFeedbackSwitch && (
                                <div
                                  className={`feedback-switch-wrap${agent._promptModified ? ' avatar-tooltip' : ''}`}
                                  data-tooltip={agent._promptModified
                                    ? 'Prompt changed. Save and regenerate to enable Quick Feedback.'
                                    : undefined}
                                >
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!isQuickFeedbackMode}
                                    aria-label="Toggle thinking mode"
                                    className={`feedback-switch ${!isQuickFeedbackMode ? 'on' : 'off'}${manualNeedsQuickFeedback ? ' feedback-blocked' : ''}`}
                                    onClick={() => {
                                      if (agent._promptModified) return;
                                      setAgentQuickEdit(prev => ({ ...prev, [agent.id]: !prev[agent.id] }));
                                    }}
                                    disabled={!!agent._promptModified}
                                  >
                                    <span className="feedback-switch-knob"></span>
                                  </button>
                                  <span className={`feedback-switch-label ${isQuickFeedbackMode ? 'quick' : 'think'}${manualNeedsQuickFeedback ? ' feedback-blocked' : ''}`}>
                                    <i className={`bi ${isQuickFeedbackMode ? 'bi-lightning-charge-fill' : 'bi-stars'} me-1`}></i>
                                    {isQuickFeedbackMode ? 'Quick feedback' : 'Thinking'}
                                  </span>
                                </div>
                              )}
                            </div>
                            {agentHasComments && commentData && (
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="comment-count-badge"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openOriginalCommentsModal(sectionKey, agentKey);
                                  }}
                                >
                                  <i className="bi bi-chat-left-text-fill"></i>
                                  {commentData.comment_count} comment{commentData.comment_count !== 1 ? 's' : ''}
                                </button>
                                <button
                                  type="button"
                                  className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openDiscardCommentModal(sectionKey, agentKey);
                                  }}
                                >
                                  <i className="bi bi-trash"> </i> <span></span>
                                  Discard Comment
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="feedback-input-container">
                            {agentSelectedText[agent.id] && (
                              <button
                                type="button"
                                className={`feedback-chevron-btn ${agentPreviewOpen[agent.id] ? 'open' : ''}`}
                                onClick={() => togglePreviewOpen(agent.id)}
                                title={agentPreviewOpen[agent.id] ? 'Hide attached text' : 'Show attached text'}
                                disabled={isLocked || docsStale}
                              >
                                <i className="bi bi-chevron-up"></i>
                                {!agentPreviewOpen[agent.id] && <span className="feedback-chevron-dot" />}
                              </button>
                            )}
                            {feedbackBlocked && !agentSelectedText[agent.id] && (
                              <i className="bi bi-exclamation-triangle feedback-blocked-icon"></i>
                            )}
                            <input
                              className={`feedback-field formField4 ${agentHasComments ? 'feedback-with-comments' : ''} ${isQuickFeedbackMode ? 'feedback-field-quick' : ''} ${agentSelectedText[agent.id] ? 'feedback-with-attachment' : ''} ${agentPreviewOpen[agent.id] ? 'feedback-preview-mode' : ''} ${feedbackBlocked ? 'feedback-blocked' : ''} ${feedbackBlocked && !agentSelectedText[agent.id] ? 'feedback-with-warning' : ''}`}
                              placeholder={feedbackPlaceholder}
                              rows="2"
                              value={agentPreviewOpen[agent.id]
                                ? `"${agentSelectedText[agent.id] || ''}"`
                                : getAgentFeedback(sectionKey, agentKey)}
                              onChange={(e) => {
                                if (agentPreviewOpen[agent.id]) return; // read-only in preview mode
                                updateAgentFeedback(sectionKey, agentKey, e.target.value);
                              }}
                              readOnly={!!agentPreviewOpen[agent.id]}
                              disabled={isLocked || docsStale || manualNeedsQuickFeedback}
                            />
                            {agentSelectedText[agent.id] && agentPreviewOpen[agent.id] && (
                              <button
                                type="button"
                                className="feedback-remove-attachment"
                                onClick={() => removeSelectionAttachment(agent.id)}
                                title="Remove attached text"
                                disabled={isLocked || docsStale}
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {agent.id ? (
                        <div className={`single-agent-run-alignment ${agent.status === 'COMPLETED' ? 'single-agent-run-alignment-completed' : ''}`}>
                          <button
                            type="button"
                            className="ev-btn-gradient-fill-round ev-bold-reg"
                            onClick={() => handleRunSingleAgent(sectionKey, agentKey)}
                            disabled={isLocked || !!agentPreviewOpen[agent.id]}
                            title={agentPreviewOpen[agent.id] ? 'Close the attached-text preview to run' : undefined}
                          >
                            <i className="bi bi-play-fill me-1"></i>
                            Generate
                          </button>
                        </div>
                      ) : (
                        <div className="ev-alert-card-warning mb-0">
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          Almost ready! Click <b>Save Template </b>above to save your agent, then you can run it
                        </div>
                      )}

                    </div>

                    <div
                      className="genbi-response-wrapper"
                      ref={(el) => { if (agent.id) responseWrapperRefs.current[agent.id] = el; }}
                      onMouseUp={agent.id ? handleResponseMouseUp(agent.id) : undefined}
                    >
                      <GenbiResponseComponent
                      agent={agent}
                      status={agent.status}
                      onTranslate={handleTranslateAgent}
                      isTranslateDisabled={agent.status !== 'COMPLETED'}
                      fabricId={formData.fabric_id}
                      authToken={GetCookie('access_token')}
                      onSaveResponse={handleSaveAgentResponse}
                      referenceIndex={referenceIndex}
                      formatJson={formData.format_json || null}
                       templateId={templateId}
                      agentDocs={(agent.document_mappings || [])
                        .filter(m => m.selected && docIdToName[m.doc_id])
                        .map(m => ({ id: m.doc_id, name: docIdToName[m.doc_id] }))}
                      responseHeaderNotice={missingKnowledgeSource ? (
                        <span className="ev-alert-card-warning ev-btn-yellow-outline-round genbi-no-doc-notice ev-bold-reg d-inline-flex align-items-center ms-1">
                          <i className="bi bi-exclamation-triangle-fill me-1"></i>
                          No documents configured
                        </span>
                      ) : null}
                      headerExtra={(() => {
                        const paragraphStyleOptions = getParagraphStyleOptions();
                        return (
                          <>
                            {paragraphStyleOptions.length > 0 && !section.is_summary_section && (
                          <div
                            ref={openParagraphDropdown === agentKey ? dropdownRef : null}
                            className="position-relative d-inline-block"
                          >
                            <button
                              type="button"
                              className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg border-0"
                              onClick={() => toggleParagraphDropdown(agentKey)}
                            >
                              <i className="bi bi-text-paragraph me-1"></i>
                              Style
                              <i className="bi bi-chevron-down ms-1"></i>
                            </button>
                            {openParagraphDropdown === agentKey && (
                              <div className="position-absolute top-100 end-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000 p-2 rounded-2">
                                <span className="ev-small-p-blue ev-bold-med px-1 mb-1">Select Paragraph Style</span>
                                {paragraphStyleOptions.map((style) => {
                                  const isSelected = String(agent.par_style ?? "") === String(style.value ?? "");
                                  return (
                                    <button
                                      key={style.value ?? "default"}
                                      type="button"
                                      className={`par-style-item d-flex align-items-center ev-fs-12 py-2 px-2 ${isSelected ? "active" : ""}`}
                                      onClick={() => {
                                        const newValue = isSelected ? null : style.value;
                                        updateAgentField(sectionKey, agentKey, "par_style", newValue);
                                        closeParagraphDropdown();
                                      }}
                                    >
                                      <i className={`bi ${isSelected ? "bi-check-circle-fill" : "bi-circle"} me-2`}></i>
                                      {style.label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                            )}
                          </>
                        );
                      })()}
                    />
                      {selectionFloatBtn
                        && selectionFloatBtn.agentId === agent.id
                        && agent.status === 'COMPLETED'
                        && !isLocked && (
                        <button
                          type="button"
                          className="selection-attach-btn"
                          style={{ left: `${selectionFloatBtn.x}px`, top: `${selectionFloatBtn.y}px` }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={attachSelectionToFeedback}
                        >
                          <i className="bi bi-quote me-1"></i>
                          Add in feedback
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-1">
                  <div className="ev-message-card2 me-1 ms-2 template-caret-card " >
                    <button
                      type="button"
                      className="template-ordering-btn"
                      onClick={() => moveAgentOrder(sectionKey, agentKey, 'up')}
                      disabled={agentIndex === 0}
                    >
                      <i className="bi bi-caret-up-square"></i>
                    </button>
                    <button
                      type="button"
                      className="template-ordering-btn ms-1"
                      onClick={() => moveAgentOrder(sectionKey, agentKey, 'down')}
                      disabled={agentIndex === section.agentOrder.length - 1}
                    >
                      <i className="bi bi-caret-down-square"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const LoadingOverlay = () => {
    if (!isLoading) return null;

    return (
      <div className="template-overlay">
        <div className="text-center">
          <div className="template-loader"></div>
        </div>
      </div>
    );
  };

  if (isInitialLoading) {
    return null;
  }

  // Pagination for comment modal
  const totalCommentPages = Math.ceil(commentFeedbackList.length / COMMENTS_PER_PAGE);
  const paginatedComments = commentFeedbackList.slice(
    (commentPage - 1) * COMMENTS_PER_PAGE,
    commentPage * COMMENTS_PER_PAGE
  );

  return (
    <div>
      <LoadingOverlay />

      {/* Hidden file input for comment upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".docx"
        style={{ display: 'none' }}
        onChange={handleCommentedDocumentUpload}
      />

      <div className="row mb-2 ev-card-5 w-100 m-0 ev-padding-xsmall ev-radius-large flex-nowrap">
        <div className="col-auto p-0">
          <div className="ev-message-card3 ev-btn-small">
            <span className="ev-small-p-blue avatar-tooltip" data-tooltip={formData.template_name}>
              <i className="bi bi-kanban me-1"></i>
              <TrimContent text={formData.template_name} maxLength={20} />
            </span>
          </div>
        </div>
          <div className="col pe-0 d-flex justify-content-end align-items-center">
          <button
            type="button"
            className="ev-btn-blue-outline-round d-flex align-items-center gap-1 me-1 ev-btn-small avatar-tooltip text-nowrap"
            onClick={handleSyncDocs}
            data-tooltip="Re-match documents to agents by tags"
          >
            <i className="bi bi-database-check"></i>
            Sync Tags
          </button>
          <span className="me-1"> <StatusBadgeSm status={templateData.status} /></span>
        <div className="ev-message-card5 m-0 ev-padding-xsmall2 d-flex align-items-center ms-1">
             {/* --- HOVERABLE AGENT STATUS (INLINE) --- */}
                 {templateData && (
              <div
                className="d-inline-block position-relative"
                onMouseEnter={() => setIsStatusDropdownOpen(true)}
                onMouseLeave={() => setIsStatusDropdownOpen(false)}
              >
                <div className="ev-message-card2 rounded-pill ev-btn-xsmall ev-bold-reg text-nowrap d-inline-flex align-items-center cursor-pointer">
                  <b>{templateData.agent_count} Agents</b>
                  <i
                    className={`bi ${isStatusDropdownOpen ? "bi-arrow-down" : "bi-arrow-right"} ms-1 opacity-30`}
                  ></i>
                </div>

                {isStatusDropdownOpen && (
                  <div className="position-absolute top-100 start-50 translate-middle-x mt-2 z-1000">
                    {/* Modified Square-ish Design Wrapper */}
                    <div className="ev-message-card5 m-0 ev-padding-xsmall2 ps-0 d-flex align-items-center ms-1 shadow-lg">
                      <div className="d-flex gap-2 align-items-center">
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Done: {templateData.completed_agents || 0}</b>
                        </div>
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Running: {templateData.running_agents || 0}</b>
                        </div>
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Pending: {templateData.pending_agents || 0}</b>
                        </div>
                        <div className="ev-message-card2 rounded-pill ev-btn-xsmall ms-1 text-nowrap">
                          <b>Failed: {templateData.failed_agents || 0}</b>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            className='ev-btn-blue-fill-round ev-bold-reg ms-1 avatar-tooltip text-nowrap ev-btn-small' 
            disabled={templateData.running_agents > 0 || templateData.report_gen_status === 'RUNNING'}
            onClick={showGenReportModal}
            data-tooltip={
              templateData.report_gen_status === 'RUNNING'
                ? "Report generation is already in progress"
                : templateData.running_agents > 0
                  ? "Wait for agents to complete before generating report"
                  : "Generate report in Word and PDF format"
            }
          >
            <i className="bi bi-file-text-fill"></i> Generate Report
          </button>

          <button
            className="ev-btn-lightblue-fill-round ev-bold-reg ms-2 ev-btn-small"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <i className="bi bi-arrow-repeat me-1"></i>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {!error && (
        <div>
          {formData.sectionOrder.length === 0 && (
            <div className="ev-alert-card-warning mt-2 mb-2">
              <p className="mb-0">
                <i className="bi bi-info-circle me-1"></i>
                &nbsp;
                Click "Super Section" to add your first section
              </p>
            </div>
          )}

          <div className="ev-card-3 mb-0 mt-1 ev-padding-small">
            <div className="d-flex justify-content-between align-items-center mb-2 text-nowrap">
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  className="ev-btn-blue-fill-round ev-bold-reg ev-btn-small"
                  onClick={() => addSection()}
                >
                  <i className="bi bi-plus-circle"></i> Super Section
                </button>
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-1 avatar-tooltip"
                  onClick={addSummarySection}
                  data-tooltip="Add a Special Section that will be placed on starting of document"
                >
                  <i className="bi bi-journal-text"></i> Special Section
                </button>
                <button
                  type="button"
                  className="ev-btn-light-fill ev-bold-reg ms-2 avatar-tooltip ev-btn-small"
                  onClick={handleToggleExpandCollapseAll}
                  disabled={getExpandableSectionKeys().length === 0}
                  data-tooltip={areAllExpandableExpanded() ? "Collapse all Sections & Agents" : "Expand all Sections & Agents"}
                >
                  <i className={`bi ${areAllExpandableExpanded() ? "bi-arrows-collapse" : "bi-arrows-expand"} me-1`}></i>
                  {areAllExpandableExpanded() ? "Collapse All" : "Expand All"}
                </button>

                
                {/* Filter & Select UI — single button directly opens the picker modal */}
                <div className="d-flex align-items-center ms-1 p-0 ev-card-7 rounded-pill">
                  <button
                    type="button"
                    className="genbi-custom-btn-group-inner ev-btn-blue-outline-round d-flex align-items-center gap-1"
                    onClick={() => setFilterPickerOpen(true)}
                  >
                    <i className="bi bi-funnel-fill"></i>
                    Filter Agents
                    {agentFilter.length > 0 && !agentFilter.includes('ALL') && (
                      <span className="ev-fs-10">({agentFilter.length})</span>
                    )}
                  </button>

                  {(() => {
                    const allVisibleAgentIds = formData.sectionOrder
                      .filter(sectionKey => !formData.sectionsDict[sectionKey]?.parent_section_id)
                      .flatMap(sectionKey => getVisibleAgentIdsInSection(sectionKey));
                    const allSelected = allVisibleAgentIds.length > 0 && allVisibleAgentIds.every(id => selectedAgentIds.has(id));
                    return (
                      <button
                        type="button"
                        className="ev-message-card4 ms-2 rounded-pill px-2 py-1 ev-fs-10 border-0"
                        onClick={() => allSelected ? deselectAllVisible() : selectAllVisible()}
                      >
                        {allSelected
                          ? 'Deselect All'
                          : selectedAgentIds.size > 0
                            ? `${selectedAgentIds.size} Selected`
                            : 'Select All'}
                      </button>
                    );
                  })()}
                </div>

              </div>

              <div className="d-flex align-items-center">
                {hasPendingChanges && promptVersionPreviewAgents.size === 0 && (
                  <button
                    className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                    onClick={handleSaveEdits}
                    data-tooltip={
                      templateData.report_gen_status === 'RUNNING'
                        ? "Report generation is in progress, please wait"
                        : templateData.running_agents > 0
                          ? "Wait for agents to complete"
                          : undefined
                    }
                    disabled={isLoading || templateData.report_gen_status === 'RUNNING' || templateData.running_agents > 0}
                  >
                    <i className="bi bi-check-circle me-1"></i>
                    Save Template
                  </button>
                )}
                
                {hasAgentsWithFeedback() && (
                  <>
                    {!hasAnyQuickFeedbackAgent() && (
                      getSelectedAgentsWithFeedbackCount() > 0 ? (
                        <button
                          className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                          onClick={() => handleVerifyAndAddressComments(true)}
                          disabled={isLoading}
                          data-tooltip="Run only selected agents with feedback"
                        >
                          <i className="bi bi-check2-square me-1"></i>
                          Address ({getSelectedAgentsWithFeedbackCount()})
                        </button>
                      ) : (
                        <button
                          className="ev-btn-yellow-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                          onClick={() => handleVerifyAndAddressComments(false)}
                          disabled={isLoading}
                          data-tooltip="Run all agents with feedback to address comments"
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Address All
                        </button>
                      )
                    )}
                    
                    <button
                      className="ev-btn-red-fill-round ev-bold-reg ms-1 avatar-tooltip ev-btn-small"
                      onClick={() => setShowDiscardAllCommentsModal(true)}
                      disabled={isLoading}
                      data-tooltip="Remove all comments and feedback"
                    >
                      <i className="bi bi-trash me-1"></i>
                      Discard All
                    </button>
                  </>
                )}

                <div className="btn-group custom-btn-group-outer ms-2">
                  <button
                    className={`btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round avatar-tooltip`}
                    data-tooltip={
                      hasSelectedQuickFeedbackAgent()
                        ? 'Use generate button for quick feedback'
                        : selectedAgentIds.size > 0
                          ? `Run ${selectedAgentIds.size} selected agents`
                          : 'Select at least one agent to run'
                    }
                    disabled={hasSelectedQuickFeedbackAgent()}
                    onClick={() => {
                      if (selectedAgentIds.size === 0) {
                        onError('Please select at least one agent to run.');
                        return;
                      }
                      handleRunConfirmation('selective_run');
                    }}
                  >
                    <i className="bi bi-play-fill"></i> Run
                  </button>
                </div>

                
                <ToggleMenu
                  isLoading={isLoading}
                  templateData={templateData}
                  setTemplateData={setTemplateData}
                  fabricId={formData.fabric_id}
                  templateId={templateId}
                  handleRefresh={handleRefresh}
                  onUploadCommentedReport={triggerCommentUpload}
                />
              </div>
            </div>

             <div
              ref={scrollContainerRef}
              className="scrollable-element vh-65"
            >
              {formData.sectionOrder.some(key => isSectionVisibleUnderFilter(key)) ? (
                formData.sectionOrder
                  .slice()
                  .sort((a, b) => {
                    const orderingA = formData.sectionsDict[a]?.section_ordering_id || '0';
                    const orderingB = formData.sectionsDict[b]?.section_ordering_id || '0';
                    return naturalCompare(orderingA, orderingB);
                  })
                  .map((sectionKey) => renderSection(sectionKey))
              ) : (
                formData.sectionOrder.length > 0 && (
  /* CHANGE: simple warning card instead of custom empty state block */
  <div className="ev-alert-card-warning mt-2 mb-2">
    <p className="mb-0">
      <i className="bi bi-info-circle me-1"></i>
      &nbsp;No agents available with the selected filter{agentFilter.length > 1 ? 's' : ''}: <b>{agentFilter.join(', ')}</b>
    </p>
  </div>
)
              )}
            </div>
          </div>
        </div>
      )}

{/* Discard Comment Modal */}
{showDiscardCommentModal && discardCommentInfo && (
  <div className="comment-success-modal-overlay" onClick={() => setShowDiscardCommentModal(false)}>
    <div className="comment-success-modal ev-message-card2">
    <div className="comment-success-modal-header" onClick={(e) => e.stopPropagation()}>
    
        <p className="mb-0 ev-med-p-blue comment-success-modal-title">
          Discard Comment
        </p>
        <button
          type="button"
          className="ev-btn-text-only-fill ev-fs-20"
          onClick={() => setShowDiscardCommentModal(false)}
        >
          <i className="bi bi-x"></i>
        </button>
      </div>
      <div className="comment-success-modal-body scrollable-element">
        <div className="ev-alert-card-warning mb-2">
          <i className="bi bi-exclamation-triangle-fill"></i>

            <strong>Warning:</strong> This action cannot be undone. The feedback and all original comments for this agent will be permanently removed.

        </div>
        
        <div className="discard-comment-info">
          <div className="discard-comment-info-item">
            <div className="discard-comment-info-label">Section & Agent:</div>
            <div className="discard-comment-info-value">
              Section {discardCommentInfo.sectionOrdering}: {discardCommentInfo.sectionName} - 
              Agent {discardCommentInfo.agentOrdering}: {discardCommentInfo.agentName}
            </div>
          </div>
          
          <div className="discard-comment-info-item">
            <div className="discard-comment-info-label">Consolidated Feedback:</div>
            <div className="discard-comment-info-value">
              {discardCommentInfo.consolidatedFeedback}
            </div>
          </div>
          
          {discardCommentInfo.originalComments && discardCommentInfo.originalComments.length > 0 && (
            <div className="discard-comment-info-item">
              <div className="discard-comment-info-label">
                Original Comments ({discardCommentInfo.commentCount}):
              </div>
              <div className="discard-comment-info-value">
                {discardCommentInfo.originalComments.map((comment, idx) => (
                  <div key={idx} className="original-comment-list-item">
                    {idx + 1}. "{comment}"
                  </div>
                ))}
              </div>
            </div>
          )}
          
        </div>
      </div>
      <div className="comment-success-modal-footer">
        <button
          type="button"
          className="ev-btn-blue-outline-round ev-bold-reg me-2"
          onClick={() => setShowDiscardCommentModal(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ev-btn-red-fill-round ev-bold-reg"
          onClick={executeDiscardComment}
        >
          <i className="bi bi-trash me-1"></i>
          Yes, Delete
        </button>
      </div>
    </div>
    </div>

)}

{/* Comment Success Modal with Navigation */}
{showCommentSuccessModal && commentFeedbackList.length > 0 && createPortal(
  <div
    className="modal fade show d-block bg-dark bg-opacity-50"
    style={{ zIndex: 'var(--num-10000000000)' }}
  >
    <div className="modal-dialog modal-lg modal-dialog-centered">
      <div className="modal-content comment-success-modal ev-message-card2">
      <div className="comment-success-modal-header">
        <h5 className="comment-success-modal-title">
          <i className="bi bi-check-circle-fill text-success"></i>
          Comments Processed Successfully!
        </h5>
        <button
          type="button"
          className="btn-close"
          onClick={() => {
            setShowCommentSuccessModal(false);
            scrollToFirstCommentedAgent(agentsWithComments);
          }}
        ></button>
      </div>
      <div className="comment-success-modal-body">
        <p className="ev-small-p-blue mb-3">
          Your feedback has been consolidated and is ready for review. Navigate through the sections below:
        </p>
        
        {/* Comment Navigation Structure */}
        <div className="comment-nav-container scrollable-element" style={{ overflowX: 'auto' }}>
          {Object.values(buildCommentNavigationStructure())
            .sort((a, b) => naturalCompare(a.sectionOrdering, b.sectionOrdering))
            .map(sectionData => (
            <div key={sectionData.sectionKey} className="comment-nav-section">
              <div 
                className={`comment-nav-section-header ${commentNavExpanded[sectionData.sectionKey] ? 'expanded' : ''}`}
                onClick={() => toggleCommentNavSection(sectionData.sectionKey)}
              >
                <i className="bi bi-chevron-right chevron"></i>
                <span className="comment-nav-section-title">
                  Section {sectionData.sectionOrdering}: {sectionData.sectionName}
                </span>
                <span className="comment-nav-section-badge">
                  {countCommentsInSection(sectionData)} Sub Sections
                </span>
              </div>
              
              {commentNavExpanded[sectionData.sectionKey] && (
                <div className="comment-nav-subsection">
                  {/* Direct agents in parent section */}
                  {sectionData.agents
                    .sort((a, b) => a.agentOrdering - b.agentOrdering)
                    .map(agent => (
                    <div key={agent.agentId} className="comment-nav-agent-wrapper">
                      <div 
                        className="comment-nav-agent-item"
                        onClick={() => toggleCommentNavSection(`agent-${agent.agentId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className={`bi ${commentNavExpanded[`agent-${agent.agentId}`] ? 'bi-chevron-down' : 'bi-chevron-right'} comment-nav-agent-chevron`}></i>
                        <i className="bi bi-robot comment-nav-agent-icon"></i>
                        <span>
                          {agent.sectionOrdering}.{agent.agentOrdering} - {agent.agentName}
                        </span>
                        <span className="ms-auto comment-count-badge-sm">
                          {agent.commentData?.comment_count || 0} comments
                        </span>
                      </div>
                      
                      {commentNavExpanded[`agent-${agent.agentId}`] && agent.commentData && (
                        <div className="comment-nav-agent-details">
                          <div className="consolidated-feedback-section">
                            <p className="ev-small-p-blue">Consolidated Feedback:</p>
                            <div className="consolidated-feedback-text-inline">
                              {agent.commentData.consolidated_feedback}
                            </div>
                          </div>
                          
                          {agent.commentData.original_comments && agent.commentData.original_comments.length > 0 && (
                            <div className="original-comments-section">
                              <p className="ev-small-p-blue">Original comments ({agent.commentData.comment_count}):</p>
                              {agent.commentData.original_comments.map((comment, idx) => (
                                <div key={idx} className="original-comment-item-inline">
                                  "{comment}"
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Subsections */}
                  {Object.values(sectionData.subsections)
                    .sort((a, b) => naturalCompare(a.sectionOrdering, b.sectionOrdering))
                    .map(subsection => (
                    <div key={subsection.sectionKey}>
                      <div className="ev-small-p-blue comment-nav-subsection-item">
                        Section {subsection.sectionOrdering}: {subsection.sectionName}
                      </div>
                      {subsection.agents
                        .sort((a, b) => a.agentOrdering - b.agentOrdering)
                        .map(agent => (
                        <div key={agent.agentId} className="comment-nav-agent-wrapper">
                          <div 
                            className="comment-nav-agent-item"
                            onClick={() => toggleCommentNavSection(`agent-${agent.agentId}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <i className={`bi ${commentNavExpanded[`agent-${agent.agentId}`] ? 'bi-chevron-down' : 'bi-chevron-right'} comment-nav-agent-chevron`}></i>
                            <i className="bi bi-robot comment-nav-agent-icon"></i>
                            <span className='ev-small-p-blue'>
                              {agent.sectionOrdering}.{agent.agentOrdering} - {agent.agentName}
                            </span>
                            <span className="ms-auto comment-count-badge-sm">
                              {agent.commentData?.comment_count || 0} comments
                            </span>
                          </div>
                          
                          {commentNavExpanded[`agent-${agent.agentId}`] && agent.commentData && (
                            <div className="comment-nav-agent-details">
                              <div className="ev-small-p-blue consolidated-feedback-section">
                                <p className="ev-small-p-blue">Consolidated Feedback:</p>
                                <div className="consolidated-feedback-text-inline">
                                  {agent.commentData.consolidated_feedback}
                                </div>
                              </div>
                              
                              {agent.commentData.original_comments && agent.commentData.original_comments.length > 0 && (
                                <div className="ev-small-p-blue original-comments-section">
                                  <p className="ev-small-p-blue">Original comments ({agent.commentData.comment_count}):</p>
                                  {agent.commentData.original_comments.map((comment, idx) => (
                                    <div key={idx} className="original-comment-item-inline">
                                      "{comment}"
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="comment-success-modal-footer">
        <button
          type="button"
          className="ev-btn-blue-fill-round ev-bold-reg"
          onClick={() => {
            setShowCommentSuccessModal(false);
            scrollToFirstCommentedAgent(agentsWithComments);
          }}
        >
          Continue
        </button>
      </div>
      </div>
    </div>
  </div>,
  document.body
)}

    {/* Original Comments Modal */}
    {originalCommentsModalOpen && selectedAgentForComments && (
      <div className="original-comments-modal-overlay" onClick={() => setOriginalCommentsModalOpen(false)}>
        <div className="original-comments-modal ev-message-card2" onClick={(e) => e.stopPropagation()}>
          <div className="original-comments-modal-header">
            <h5 className="original-comments-modal-title">
              <i className="bi bi-chat-left-text-fill"></i>
              Original Comments
            </h5>
            <button
                            type="button"
                            className="ev-btn-text-only-fill ev-fs-20"
                            onClick={() => setOriginalCommentsModalOpen(false)}
                        >
                            <i className="bi bi-x"></i>
                        </button>
          </div>
          <div className="original-comments-modal-body scrollable-element vh-50">
            {(() => {
              const commentData = getCommentData(
                selectedAgentForComments.sectionKey,
                selectedAgentForComments.agentKey
              );
              
              if (!commentData || !commentData.original_comments || commentData.original_comments.length === 0) {
                return (
                  <div className="no-comments-message">
                    <i className="bi bi-chat-dots" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}></i>
                    No original comments available
                  </div>
                );
              }
              
              return (
                <div>
                  {commentData.original_comments.map((comment, idx) => (
                    <div key={idx} className="original-comment-list-item">
                      <strong>Comment {idx + 1}:</strong><br/>
                      "{comment}"
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    )}
{/* Verify Comments Confirmation Modal */}
{/* Discard All Comments Confirmation Modal */}
<ConfirmationModal
  show={showDiscardAllCommentsModal}
  onClose={() => setShowDiscardAllCommentsModal(false)}
  onConfirm={executeDiscardAllComments}
  title="Discard All Comments"
  message="This will remove all comments and feedback from all agents. You will need to upload the commented document again to restore them. Do you wish to continue?"
  confirmButtonText="Yes, Discard All"
  confirmButtonClass="btn-danger"
/>
  <ConfirmationModal
    show={showVerifyCommentsConfirmation}
    onClose={() => setShowVerifyCommentsConfirmation(false)}
    onConfirm={executeVerifyAndAddressComments}
    title={verifyCommentsMode === 'selected' ? "Address Selected Feedback" : "Verify and Address All Feedback"}
    message={`This will re-run ${verifyCommentsCount} agent(s) with the provided feedback. Continue?`}
    confirmButtonText={verifyCommentsMode === 'selected' ? "Yes, Address Selected" : "Yes, Address All"}
    confirmButtonClass="btn-primary"
  />

  <ConfirmationModal
    show={restoreVersionInfo.isOpen}
    onClose={() => setRestoreVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '', docsChanged: false, restoreDocs: false, versionDocIds: [], currentDocIds: [], docNameMap: {} })}
    onConfirm={executeRestoreVersion}
    title="Confirm Restore Version"
    message={`This is a destructive action. Are you sure you want to restore to ${restoreVersionInfo.versionName}? All newer versions will be permanently deleted.`}
    confirmButtonText="Yes, Restore"
    confirmButtonClass="btn-warning"
    bodyContent={restoreVersionInfo.docsChanged ? (
      <div className="mt-2">
        <div className="alert alert-warning d-flex align-items-start py-2 ev-fs-12 mb-2">
          <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
          <span>This version was generated with different documents than currently configured.</span>
        </div>
        <div className="form-check p-2 rounded" style={{ background: 'rgba(13,110,253,0.06)' }}>
          <input
            className="form-check-input ms-0 me-2"
            type="checkbox"
            id="restoreDocsCheck"
            checked={restoreVersionInfo.restoreDocs}
            onChange={(e) => setRestoreVersionInfo(prev => ({ ...prev, restoreDocs: e.target.checked }))}
          />
          <label className="form-check-label ev-fs-12 fw-semibold" htmlFor="restoreDocsCheck">
            Also restore document configuration
            <div className="fw-normal text-muted ev-fs-11">Selects the documents this version was generated with</div>
          </label>
        </div>
      </div>
    ) : null}
  />

  <ConfirmationModal
    show={restorePromptVersionInfo.isOpen}
    onClose={() => setRestorePromptVersionInfo({ isOpen: false, agentId: null, version: null, versionName: '' })}
    onConfirm={executeRestorePromptVersion}
    title="Confirm Restore Prompt Version"
    message={`This is a destructive action. Are you sure you want to restore to prompt ${restorePromptVersionInfo.versionName}? All newer prompt versions will be permanently deleted.`}
    confirmButtonText="Yes, Restore"
    confirmButtonClass="btn-warning"
  />

  <ConfirmationModal
    show={showPromptVersionWarning}
    onClose={() => {
      setShowPromptVersionWarning(false);
      setPendingPromptVersionShow(null);
    }}
    onConfirm={async () => {
      setShowPromptVersionWarning(false);
      if (pendingPromptVersionShow) {
        const { sectionKey, agentKey } = pendingPromptVersionShow;
        const agent = formData.sectionsDict[sectionKey]?.agentsDict[agentKey];
        if (agent) {
          const feedback = getAgentFeedback(sectionKey, agentKey);
          const finalFeedback = buildFinalFeedback(agent.id, feedback);
          // run directly without second confirmation popup
          if (onProcessingStart) onProcessingStart('Running agent');
          try {
            const response = await axios.post(
              `${API_BASE_URL}/run-template`,
              {
                template_id: templateId,
                fabric_id: fabricId,
                agent_id: agent.id,
                feedback: finalFeedback || null,
                master_feedback: null,
                enable_grounding_check: !!agentGrounding[agent.id],
                ...getAgentPersistRunOptions(agent)
              },
              { headers }
            );
            if (response.data.status === 'failed') {
              onError(response.data.message || 'Failed to run agent');
            } else {
              setAgentFeedback({});
              clearSelectionAttachments(agent.id);
              setSingleAgentExecution({ agentId: null, feedback: null });
              await handleRefresh();
            }
          } catch (err) {
            onError(err.message || 'Failed to run agent');
          } finally {
            if (onProcessingEnd) onProcessingEnd();
          }
        }
        setPendingPromptVersionShow(null);
      }
    }}
    title="Running with Latest Prompt"
    message="You are viewing an older prompt. The agent will run using the latest saved prompt, not the one you are currently previewing. Want to continue?"
    confirmButtonText="Yes, Run"
    confirmButtonClass="btn-warning"
  />

  <ConfirmationModal
    show={showRunConfirmation}
    onClose={() => setShowRunConfirmation(false)}
    onConfirm={executeRunTemplate}
    title={`Confirm ${runType === 'single_agent' ? 'Run Agent' : 'Run Template'}`}
    message="Are you sure you want to proceed?"
    confirmButtonText={runType === 'single_agent' ? 'Run Agent' : 'Run Template'}
    confirmButtonClass="btn-primary"
    bodyContent={getRunModalBodyContent()}
  />

  <ValueMessageSelector
    isOpen={vmSelectorOpen}
    onClose={() => setVmSelectorOpen(false)}
    currentSectionForVM={currentSectionForVM}
    templateId={templateId}
    fabricId={formData.fabric_id}
    fabricName={formData.fabric_name}
    sectionsDict={formData.sectionsDict}
    onSelectionSave={handleVMSelection}
  />

  <DocumentPreview
    isOpen={previewOpen}
    onClose={closePreview}
    docId={previewDocId}
    documentName={previewDocumentName}
  />

  <ReportModalForm
    isOpen={isGenReportModalVisible}
    onClose={onCloseGenerateReport}
    templateDetails={{'id':templateId, 'name': templateName}}
    updFunc={performSaveTemplate}
    formData={formData}
  />

  <AgentConfigure
    isOpen={configureModalOpen}
    onClose={() => setConfigureModalOpen(false)}
    sectionKey={currentAgentConfig.sectionKey}
    agentKey={currentAgentConfig.agentKey}
    formData={formData}
    fabricTreeData={fabricTreeData}
    updateAgentField={updateAgentField}
    templateId={templateId}
    refAgentExpanded={refAgentExpanded}
    onRefAgentExpandedChange={setRefAgentExpanded}
    groundingEnabled={(() => {
      const aId = formData.sectionsDict?.[currentAgentConfig.sectionKey]
        ?.agentsDict?.[currentAgentConfig.agentKey]?.id;
      return aId ? !!agentGrounding[aId] : false;
    })()}
    onGroundingChange={(checked) => {
      const aId = formData.sectionsDict?.[currentAgentConfig.sectionKey]
        ?.agentsDict?.[currentAgentConfig.agentKey]?.id;
      if (!aId) return; // grounding only applies to saved (runnable) agents
      setAgentGrounding(prev => ({ ...prev, [aId]: checked }));
    }}
  />

  {comparePanelState && (
    <AgentVersionComparePanel
      agentId={comparePanelState.agentId}
      versionA={comparePanelState.versionA}
      versionB={comparePanelState.versionB}
      fabricId={fabricId}
      onClose={() => setComparePanelState(null)}
    />
  )}

  <SyncDocsModal
    isOpen={syncDocsModalOpen}
    onClose={() => setSyncDocsModalOpen(false)}
    changes={syncDocsChanges}
    onApply={handleApplySyncChanges}
    isLoading={syncDocsLoading}
  />

  <FilterPickerModal
    isOpen={filterPickerOpen}
    onClose={() => setFilterPickerOpen(false)}
    formData={formData}
    agentFilter={agentFilter}
    setAgentFilter={setAgentFilter}
    selectedAgentIds={selectedAgentIds}
    toggleAgentSelection={toggleAgentSelection}
    handleSectionCheckbox={handleSectionCheckbox}
    isAgentVisible={isAgentVisible}
    isSectionVisibleUnderFilter={isSectionVisibleUnderFilter}
    getVisibleAgentIdsInSection={getVisibleAgentIdsInSection}
    selectAllVisible={selectAllVisible}
    deselectAllVisible={deselectAllVisible}
    naturalCompare={naturalCompare}
  />
</div>
);
};
export default GenbiTemplateTabViewer;
